import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { track } from "../lib/mixpanel"
import { showToast } from "../lib/toast"
import { rotatedImage } from "../lib/rotated_image_layer"
import { UPLOAD_LIMIT_BYTES, showSizeLimitModal } from "../lib/size_limit_modal"

// The Drone Flights row under Site Walks (pmfit K8-35, Larry 2026-10-08:
// DroneDeploy "shows the plans on top of it... all the different flights...
// you see how it changes from day to day"; Phelipe 2026-10-22: its own
// section, where flights are added and DroneDeploy's features land). The
// first feature, the stitched maps compared by date:
//
//   - click a date and its map lies on the satellite (by the corners the
//     GeoTIFF gave or that were lined up by hand); click a second date and
//     the two compare - the earlier on the LEFT of a line you drag, the
//     later on the right (Phelipe 2026-10-22: "select two options to compare
//     or deselect one and just leave one selected"); click a picked date
//     again to drop it, a third one takes the older pick's place;
//   - the site plan lies on top, see-through (Plans on top);
//   - the walks' GPS routes are drawn over it (Walk routes);
//   - with two dates picked, "What changed" asks the AI to say what is
//     different between them (DroneFlightCompareService), kept per pair;
//   - Add drone flight sends the file straight to S3; the server makes the
//     copy the browser shows and the row polls until it's ready.
//
// With flights, the row loads them the first time it's opened; with none,
// only the Add button is live. Area / line / point markups are for later.
const IMAGERY = "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
const PLACES = "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
// A drone map is a few centimetres a pixel: let people zoom well past the satellite's own detail.
const MAP_MAX_ZOOM = 24
const ROUTE_COLOR = "#fb7185"
const POLL_MS = 5000
const POLL_MAX = 240
// A stitch takes an hour or more: those flights are asked about every fourth tick, for as long as it runs.
const STITCH_POLL_EVERY = 4
const AI_POLL_MS = 4000
const AI_POLL_MAX = 45
// The drone's raw photos (2026-10-27): a folder of JPGs goes up one PUT
// each, a few at a time, with signed links asked for a batch at a time; a
// zip goes up in S3 parts. Every PUT that fails waits and tries again, for
// as long as it takes, so a dropped connection pauses the upload instead of
// ending it; and whatever is still missing after a reload is sent by
// dropping the same folder again (the server says which photos it has).
const PHOTO_CAP = 3000
const PHOTO_MAX_BYTES = 60 * 1024 * 1024
const ZIP_MAX_BYTES = 30 * 1024 * 1024 * 1024
const PHOTO_PARALLEL = 4
const PART_PARALLEL = 3
const PART_BATCH = 100 // part links asked for per request, the most the server hands out (part_urls); a part is 64 MB
const URL_BATCH = 200
const RECEIVED_BATCH = 100
const RETRY_MAX_MS = 60000

export default class extends Controller {
  static targets = ["threeDButton", "terrain", "pileForm", "pileFormNote", "pileName", "pileMaterial", "pileDensity", "pileDensityUnit",
    "pilesPanel", "pilesTitle", "pilesRows", "pileRemoveModal", "pileRemoveName", "count", "empty", "body", "bar", "toggle", "inside", "map", "chips", "stamp", "removeLink", "nudgeLink", "slider", "leftLabel", "rightLabel",
    "plansOn", "opacity", "planName", "routesOn", "aerialOpacity", "beforeOpacityRow", "beforeOpacity", "lineUpBar", "nudgeBar", "nudgeSave",
    "photosOn", "photosCount", "measureLine", "measureArea", "measureClear", "measureHint", "info", "downloadLink",
    "aerialOpacityValue", "beforeOpacityValue", "planOpacityValue", "lock", "lockClosed", "lockOpen", "lockText", "moveTools", "pinLink", "undoNudgeLink", "undoNudgeText", "nudgeUndo", "nudgeRedo",
    "timeline", "timelineTrack", "timelineFill", "timelineDots", "timelineKnob",
    "aiButton", "aiPanel", "aiTitle", "aiStatus", "aiList", "aiAgain",
    "addModal", "addError", "file", "folder", "dropzone", "fileChips", "flownOn", "label", "addButton", "emailRow", "emailWhenDone", "emailEta",
    "progress", "progressBar", "progressText", "readyAt", "readyAtText", "progressNote", "pauseButton", "resumeNote",
    "removeModal", "removeButton", "failReason", "failActions", "failTold", "retryLink",
    "heightsBox", "heightsRows", "heightsNote", "heightsMake", "heightsRange", "contourEvery", "reliefOn", "contoursOn", "waterOn", "reliefOpacity", "reliefOpacityValue",
    "measureVolume", "targetsButton", "targetsFile", "targetsPanel", "targetsSummary", "targetsRows"]
  static values = { url: String, pilesUrl: String, projectId: Number, projectPhase: String, center: Array, count: Number, open: Boolean, ai: Boolean, settingsUrl: String, text: Object,
    stitchPace: Number, zipPhotoBytes: Number, prefs: Object }

  connect() {
    this.flights = []
    this.walks = []
    this.photos = []
    this.plan = null
    // The dates picked, in the order picked: one shows it, two compare.
    this.picks = []
    this.comparisons = {}
    this.split = 0.5
    this.loaded = false
    this._onKey = (event) => {
      if (event.key !== "Escape") return
      if (!this.addModalTarget.classList.contains("hidden")) { event.stopImmediatePropagation(); this.closeAdd() }
      else if (!this.removeModalTarget.classList.contains("hidden")) { event.stopImmediatePropagation(); this.cancelRemove() }
    }
    document.addEventListener("keydown", this._onKey, true)
    this._applyPrefs()
    // Closed by default (Phelipe 2026-10-22): the flights load the first time it opens.
    if (this.countValue > 0 && this.openValue) this._start()
  }

  // ── On the map, remembered ──────────────────────────────────────────
  // What this person shows on this project's map and how see-through each
  // layer is (Phelipe 2026-10-28), theirs on every computer: the row comes
  // with their last choices (user_settings.drone_map_by_project) and every
  // change goes back through update_settings. Plans on top is off until ticked.
  _applyPrefs() {
    const p = this.prefsValue || {}
    const on = (el, key) => { if (typeof p[key] === "boolean") el.checked = p[key] }
    const level = (el, key) => { if (typeof p[key] === "number") el.value = String(p[key]) }
    on(this.plansOnTarget, "plans_on"); on(this.routesOnTarget, "routes_on"); on(this.photosOnTarget, "photos_on")
    on(this.reliefOnTarget, "heights_on"); on(this.contoursOnTarget, "contours_on"); on(this.waterOnTarget, "water_on")
    level(this.aerialOpacityTarget, "flight_opacity"); level(this.opacityTarget, "plan_opacity"); level(this.reliefOpacityTarget, "heights_opacity")
    this._applyingPrefs = true
    this.setAerialOpacity(); this.setOpacity(); this.setReliefOpacity()
    this._applyingPrefs = false
  }

  _prefs() {
    return { plans_on: this.plansOnTarget.checked, routes_on: this.routesOnTarget.checked, photos_on: this.photosOnTarget.checked,
      heights_on: this.reliefOnTarget.checked, contours_on: this.contoursOnTarget.checked, water_on: this.waterOnTarget.checked,
      flight_opacity: this._aerialOpacity(), plan_opacity: Number(this.opacityTarget.value) || 0.7, heights_opacity: this._reliefOpacity() }
  }

  // Saved a moment after the last change: a slider fires many times a second.
  _savePrefs() {
    if (!this.settingsUrlValue || this._applyingPrefs) return
    clearTimeout(this._prefsTimer)
    this._prefsTimer = setTimeout(async () => {
      const prefs = this._prefs()
      try {
        await fetch(this.settingsUrlValue, {
          method: "PUT", credentials: "same-origin",
          headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
          body: JSON.stringify({ setting: { drone_map_project_id: this.projectIdValue, drone_map: prefs } })
        })
        track("Drone Map Settings Saved", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, ...prefs })
      } catch (_) { /* not remembered this time */ }
    }, 600)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey, true)
    this._stopPolling()
    this._stopAiPolling()
    this._teardown()
  }

  // ── Open / closed ───────────────────────────────────────────────────
  // The title folds the section; the last state is theirs on every computer
  // (user_settings.drone_flights_open, saved through update_settings).
  toggle() {
    this.openValue = !this.openValue
    this._showInside()
    if (this.openValue && !this.loaded && this.countValue > 0) this._start()
    else if (this.openValue && this.loaded) this._paint()
    this._saveOpen()
    track("Drone Flights Section Toggled", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, open: this.openValue })
  }

  // A click on the bar itself (not on a button in it, and not inside the
  // open section) opens or closes it too.
  barClick(event) {
    if (event.target.closest("button, a, input, select, label")) return
    if (this.hasInsideTarget && this.insideTarget.contains(event.target)) return
    this.toggle()
  }

  _showInside() {
    if (!this.hasInsideTarget) return
    this.insideTarget.classList.toggle("hidden", !this.openValue)
    if (this.hasBodyTarget) this.bodyTarget.dataset.open = String(this.openValue)
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", String(this.openValue))
      this.toggleTarget.querySelector("svg")?.classList.toggle("rotate-90", this.openValue)
    }
  }

  async _saveOpen() {
    if (!this.settingsUrlValue) return
    try {
      await fetch(this.settingsUrlValue, {
        method: "PUT", credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ setting: { drone_flights_open: this.openValue } })
      })
    } catch (_) { /* not remembered this time */ }
  }

  async _start() {
    await this._load()
    this._paint()
  }

  stop(event) {
    event.stopPropagation()
  }

  async _load() {
    try {
      const data = await this._send("GET", this.urlValue)
      this.flights = data.drone_flights || []
      this.walks = data.walks || []
      this.photos = data.photos || []
      this.stockpiles = data.stockpiles || []
      this.plan = data.site_plan || null
      this.loaded = true
      if (!this.picks.length) {
        const first = this._shown()[0] || this.flights[0]
        if (first) this.picks = [first.id]
      }
    } catch (error) {
      showToast(error.message, "error")
    }
  }

  // ── The picks ───────────────────────────────────────────────────────
  // One pick: that flight shows. Two: the earlier is "before" (left of the
  // line), the later "after" (right); the stamp, Remove and the line-up bar
  // speak of the later one.

  _picked() {
    return this.picks.map((id) => this.flights.find((f) => f.id === id)).filter(Boolean)
  }

  _earlierFirst(list) {
    return [...list].sort((a, b) => (a.flown_on < b.flown_on ? -1 : a.flown_on > b.flown_on ? 1 : a.id - b.id))
  }

  _base() {
    const picked = this._picked()
    return picked.length === 2 ? this._earlierFirst(picked)[1] : picked[0] || null
  }

  _other() {
    const picked = this._picked()
    return picked.length === 2 ? this._earlierFirst(picked)[0] : null
  }

  _pick(flight) {
    if (this.nudge) this.cancelNudge()
    this.scrub = null
    const shown = flight.status === "ready" && flight.corners && flight.image_url
    if (this.picks.includes(flight.id)) {
      if (this.picks.length > 1) this.picks = this.picks.filter((id) => id !== flight.id)
    } else if (!shown) {
      this.picks = [flight.id] // a map not on the ground yet shows alone, with the line-up bar
    } else {
      this.picks = [...this.picks.filter((id) => this.flights.find((f) => f.id === id && f.corners)), flight.id].slice(-2)
    }
    this._paintChips()
    this._draw(true)
    this._paintAi()
    if (this.picks.length === 2) {
      track("Drone Flights Compared", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
        drone_flight_id: this._base()?.id, with_drone_flight_id: this._other()?.id })
    }
  }

  // ── The panel ───────────────────────────────────────────────────────

  _paint() {
    const n = this.flights.length
    this.countValue = n
    this.countTarget.textContent = n === 1 ? this.textValue.flights_one : this.textValue.flights_other.replace("__COUNT__", n)
    this.emptyTarget.classList.toggle("hidden", n > 0)
    this.bodyTarget.classList.toggle("hidden", n === 0)
    if (n === 0) {
      this._teardown()
      return
    }
    this.planNameTarget.textContent = this.plan?.corners ? this.textValue.plan_name.replace("__NAME__", this.plan.label || "") : this.textValue.no_plan
    this.opacityTarget.disabled = !this.plan?.corners
    this._paintChips()
    this._paintAi()
    this._paintTimeline()
    // Leaflet measures its box: build the map once the panel is on screen.
    requestAnimationFrame(() => {
      if (this.bodyTarget.classList.contains("hidden") || !this.openValue) return
      this._buildMap()
      this._draw(true)
    })
    if (this.flights.some((f) => f.status === "processing" || f.status === "uploading" || f.status === "stitching")) this._startPolling()
  }

  // Flights, newest first: a card picture, the day, and what it is right now.
  _paintChips() {
    const base = this._base()
    const other = this._other()
    const chips = this.flights.map((flight) => {
      const isBase = flight.id === base?.id
      const isOther = flight.id === other?.id
      const picked = isBase || isOther
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "flex items-center gap-2 shrink-0 rounded-lg border px-2 py-1.5 text-left cursor-pointer transition-colors " +
        (isBase ? "border-brand bg-blue-50" : isOther ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-white hover:border-gray-300")
      chip.setAttribute("aria-pressed", String(picked))
      const thumb = document.createElement("div")
      thumb.className = "w-14 h-10 rounded bg-gray-200 overflow-hidden shrink-0"
      if (flight.thumb_url) {
        const img = document.createElement("img")
        img.src = flight.thumb_url
        img.alt = ""
        img.loading = "lazy"
        img.className = "w-full h-full object-cover"
        thumb.append(img)
      } else if (["uploading", "processing", "stitching"].includes(flight.status)) {
        // No picture yet: a turning circle in its place while the photos go up and the map is made
        // (Phelipe 2026-10-28: "in front should we show the loading gif circle or something?").
        thumb.classList.add("flex", "items-center", "justify-center")
        thumb.innerHTML = `<svg class="w-5 h-5 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
      }
      const text = document.createElement("div")
      text.className = "min-w-0"
      const day = document.createElement("div")
      day.className = "text-[13px] font-semibold text-gray-900 whitespace-nowrap"
      day.textContent = this._day(flight)
      const sub = document.createElement("div")
      sub.className = "text-[13px] whitespace-nowrap " + (isBase ? "text-brand" : isOther ? "text-amber-700" : flight.status === "failed" ? "text-red-600" : "text-gray-500")
      sub.textContent = other ? (isOther ? this.textValue.before : isBase ? this.textValue.after : this._state(flight))
                              : (isBase ? this.textValue.selected : this._state(flight))
      text.append(day, sub)
      // "Ready around 4:10 PM" (Phelipe 2026-10-28): the clock time, under the stage and its time left.
      const ready = flight.status === "stitching" ? this._readyAround(flight.eta_seconds) : null
      if (ready) {
        const when = document.createElement("div")
        when.className = "inline-block mt-0.5 rounded bg-brand/10 px-1 text-[13px] text-brand whitespace-nowrap"
        when.textContent = ready
        text.append(when)
      }
      chip.append(thumb, text)
      chip.title = flight.label || ""
      chip.addEventListener("click", () => this._pick(flight))
      return chip
    })
    this.chipsTarget.replaceChildren(...chips)
  }

  // The "email me when the map is ready" line (only photos or a zip take long enough to leave), with
  // how long the stitch should take (Phelipe 2026-10-28: "'when it's done' is super vague"): the photos
  // dropped (a zip's guessed from its size) at the box's own pace, the sum the card's time left starts from.
  _emailRow(on) {
    this.emailRowTarget.classList.toggle("hidden", !on)
    this.emailRowTarget.classList.toggle("flex", on)
    const photos = this.photoSet ? this.photoSet.files.length : this.zipFile ? this._photosInZip(this.zipFile.size) : 0
    this.emailEtaTarget.textContent = on && photos ? ` · ${this._about(this._stitchSeconds(photos), this.textValue.stitch_takes_minutes, this.textValue.stitch_takes_hours)}` : ""
  }

  // How many photos a zip of `bytes` likely holds, until it's opened (the server's guess too).
  _photosInZip(bytes) {
    return Math.max(1, Math.round(bytes / (this.zipPhotoBytesValue || 9 * 1024 * 1024)))
  }

  // How long the stitch of `photos` should take on this box: its own pace, five minutes at the least.
  _stitchSeconds(photos) {
    return Math.max(photos * (this.stitchPaceValue || 12), 300)
  }

  // "Ready around 4:10 PM" (Phelipe 2026-10-28): the clock time `seconds` from now, rounded up to
  // 5 minutes, with the day named when it isn't today. `text` carries the words around the time.
  _readyAround(seconds, text = this.textValue.ready_around) {
    if (seconds == null) return null
    const at = new Date(Math.ceil((Date.now() + seconds * 1000) / 300000) * 300000)
    const lang = document.documentElement.lang || undefined
    const time = at.toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit" })
    const today = at.toDateString() === new Date().toDateString()
    return text.replace("__TIME__", today ? time : `${at.toLocaleDateString(lang, { weekday: "short" })} ${time}`)
  }

  // Seconds in round words: to 5 minutes under an hour, to 10 above.
  _about(seconds, minutesText, hoursText) {
    const minutes = Math.ceil(seconds / 60)
    if (minutes < 60) return minutesText.replace("__MINUTES__", String(Math.ceil(minutes / 5) * 5))
    const rounded = Math.ceil(minutes / 10) * 10
    return hoursText.replace("__HOURS__", String(Math.floor(rounded / 60))).replace("__MINUTES__", String(rounded % 60))
  }

  _state(flight) {
    if (flight.status === "failed") {
      if (flight.failure === "out_of_memory") return this.textValue.out_of_memory
      return flight.source === "photos" ? this.textValue.stitch_failed : this.textValue.failed
    }
    if (flight.status === "uploading" && flight.source === "photos") {
      return flight.photo_count
        ? this.textValue.sending_short.replace("__DONE__", (flight.received_count || 0).toLocaleString()).replace("__COUNT__", flight.photo_count.toLocaleString())
        : this.textValue.sending_zip_short
    }
    // The stitch's stage (an hour or more all told), when the server says which.
    if (flight.status === "stitching") {
      // The stage alone: "Ready around 4:05 PM" under it says when (Phelipe 2026-10-28: "we don't need
      // this here either, since we have the ETA time below").
      return (flight.stitch_stage && this.textValue.stages?.[flight.stitch_stage]) || this.textValue.stitching
    }
    if (flight.status !== "ready") return this.textValue.getting_ready
    if (!flight.corners) return this.textValue.line_up
    return this._weekday(flight)
  }

  // The flights that can be shown: ready and on the ground.
  _shown() {
    return this.flights.filter((f) => f.status === "ready" && f.corners && f.image_url)
  }

  // ── The map ─────────────────────────────────────────────────────────

  _buildMap() {
    if (this.map) return
    const map = L.map(this.mapTarget, { zoomControl: true, attributionControl: true, maxZoom: MAP_MAX_ZOOM })
    map.attributionControl.setPrefix(false)
    L.tileLayer(IMAGERY, { maxZoom: MAP_MAX_ZOOM, maxNativeZoom: 19, attribution: "Imagery © Esri" }).addTo(map)
    L.tileLayer(PLACES, { maxZoom: MAP_MAX_ZOOM, maxNativeZoom: 19, opacity: 0.85 }).addTo(map)
    // The later flight, the earlier one on the left of the line, and the plan on top of both.
    map.createPane("droneBase").style.zIndex = 320
    map.createPane("droneScrub").style.zIndex = 325
    map.createPane("droneCompare").style.zIndex = 330
    // The heights (phase 2) over the aerials, under the plan.
    map.createPane("droneHeights").style.zIndex = 335
    map.createPane("droneContours").style.zIndex = 336
    map.createPane("droneWater").style.zIndex = 337
    map.createPane("sitePlan").style.zIndex = 350
    L.control.scale({ imperial: true, metric: true, position: "bottomright" }).addTo(map)
    map.on("move zoom viewreset", () => this._clip())
    map.on("click", (e) => this._mapClick(e))
    map.on("dblclick", (e) => this._mapDoubleClick(e))
    if (this.centerValue?.length === 2) map.setView(this.centerValue, 18)
    else map.setView([39.5, -98.35], 4)
    this.map = map
    this.sliderTarget.style.left = `${this.split * 100}%`
  }

  _draw(fit = false) {
    if (!this.map) return
    const flight = this._base()
    this.baseLayer?.remove()
    this.baseLayer = null
    const shown = flight && flight.status === "ready" && flight.corners && flight.image_url
    if (shown) {
      this.baseLayer = rotatedImage(L, flight.image_url, flight.corners, { width: flight.width, height: flight.height, opacity: this._aerialOpacity(), pane: "droneBase" }).addTo(this.map)
      if (fit && !this._framed) {
        this.map.fitBounds(flight.corners, { padding: [16, 16] })
        this._framed = true
      }
    } else if (fit && this.plan?.corners && !this._framed) {
      this.map.fitBounds(this.plan.corners, { padding: [16, 16] })
      this._framed = true
    }
    this.stampTarget.textContent = flight ? this._stamp(flight) : ""
    this.stampTarget.classList.toggle("hidden", !flight)
    this.removeLinkTarget.classList.toggle("hidden", !flight?.mine)
    this.retryLinkTarget.classList.toggle("hidden", flight?.status !== "failed")
    this._paintMoveTools()
    this.lineUpBarTarget.classList.toggle("hidden", !(flight && flight.status === "ready" && !flight.corners))
    this._paintInfo(flight)
    this._drawPlan()
    this._drawRoutes()
    this._drawPhotos()
    this._drawHeights()
    this._drawTargets()
    this._drawPiles()
    this._drawCompare()
    this._drawScrub()
    this._paintTimeline()
    requestAnimationFrame(() => this.map?.invalidateSize())
  }

  // "This flight" in the column: date, time, name, source, detail, how it was placed, the file.
  _paintInfo(flight) {
    if (!this.hasInfoTarget) return
    if (!flight) {
      this.infoTarget.replaceChildren()
      if (this.hasDownloadLinkTarget) this.downloadLinkTarget.classList.add("hidden")
      this.failReasonTarget.classList.add("hidden")
      this.failActionsTarget.classList.add("hidden")
      return
    }
    // Why it failed, in plain words: out of memory in the person's own language, anything else as the server said it.
    const reason = flight.status === "failed" ? (flight.failure === "out_of_memory" ? this.textValue.out_of_memory_long : flight.error) : null
    this.failReasonTarget.textContent = reason || ""
    this.failReasonTarget.classList.toggle("hidden", !reason)
    // "Our team has been told (#63) · Tell us more" (Phelipe 2026-10-28): the item ConsLog opened for it, and a note of their own.
    this.failToldTarget.textContent = flight.status === "failed" && flight.issue_ref ? this.textValue.team_told_ref.replace("__REF__", flight.issue_ref) : ""
    this.failActionsTarget.classList.toggle("hidden", flight.status !== "failed")
    const lines = this._stamp(flight).split(" · ")
    this.infoTarget.replaceChildren(...lines.map((text, i) => {
      const div = document.createElement("div")
      div.textContent = text
      if (i === 0) div.className = "font-semibold text-gray-900"
      return div
    }))
    if (this.hasDownloadLinkTarget) {
      this.downloadLinkTarget.classList.toggle("hidden", !flight.original_url)
      if (flight.original_url) this.downloadLinkTarget.href = flight.original_url
    }
  }

  fitFlight() {
    const flight = this._base()
    if (!this.map) return
    if (flight?.corners) this.map.fitBounds(flight.corners, { padding: [16, 16] })
    else if (this.plan?.corners) this.map.fitBounds(this.plan.corners, { padding: [16, 16] })
  }

  // ── Photos on the map ───────────────────────────────────────────────
  // The project's photos that know where they were taken: a pin each, the
  // picture and its day on click.
  _drawPhotos() {
    this.photoLayer?.remove()
    this.photoLayer = null
    if (this.hasPhotosCountTarget) this.photosCountTarget.textContent = this.photos.length ? `(${this.photos.length})` : ""
    if (!this.map || !this.photosOnTarget.checked || !this.photos.length) return
    this.photoLayer = L.layerGroup(this.photos.map((photo) => {
      const marker = L.circleMarker([photo.lat, photo.lon], { radius: 6, color: "#ffffff", weight: 2, fillColor: "#2563eb", fillOpacity: 1 })
      const box = document.createElement("div")
      box.className = "w-40"
      if (photo.thumbnail_url) {
        const img = document.createElement("img")
        img.src = photo.thumbnail_url
        img.alt = ""
        img.className = "w-40 h-28 object-cover rounded"
        box.append(img)
      }
      const day = document.createElement("div")
      day.className = "mt-1 text-[13px] text-gray-700"
      day.textContent = photo.taken_label || ""
      box.append(day)
      marker.bindPopup(box, { closeButton: false, offset: [0, -4] })
      return marker
    })).addTo(this.map)
  }

  togglePhotos() {
    this._drawPhotos()
    this._savePrefs()
  }

  // ── Heights (phase 2, 2026-10-28) ───────────────────────────────────
  // The ground coloured by height, the contour lines and where water
  // flows: layers over the flight shown, made from the heights the stitch
  // saved next to the map (so only a flight from the drone's photos has
  // them). The box in the column says when they're still being made, or
  // why there are none.
  _drawHeights() {
    for (const name of ["reliefLayer", "contoursLayer", "waterLayer"]) {
      this[name]?.remove()
      this[name] = null
    }
    if (!this.hasHeightsBoxTarget) return
    const flight = this._base()
    const elev = flight?.elevation
    const shown = Boolean(flight && flight.status === "ready" && flight.corners && elev)
    const ready = shown && elev.status === "ready"
    this.heightsBoxTarget.classList.toggle("hidden", !shown)
    if (this.hasMeasureVolumeTarget) this.measureVolumeTarget.classList.toggle("hidden", !ready)
    if (this.hasTargetsButtonTarget) this.targetsButtonTarget.classList.toggle("hidden", !ready)
    if (this.hasThreeDButtonTarget) this.threeDButtonTarget.classList.toggle("hidden", !ready)
    if (!shown) return
    this.heightsRowsTarget.classList.toggle("hidden", !ready)
    this.heightsNoteTarget.classList.toggle("hidden", ready)
    this.heightsMakeTarget.classList.toggle("hidden", ready || !elev.possible || elev.status === "making")
    if (!ready) {
      this.heightsNoteTarget.textContent = elev.status === "making" ? this.textValue.elev_making
        : elev.status === "failed" ? this.textValue.elev_failed.replace("__ERROR__", elev.error || "")
        : elev.possible ? "" : this.textValue.elev_none
      if (elev.status === "making") this._watchHeights(flight.id)
      return
    }
    const lo = Number(elev.min_m)
    const hi = Number(elev.max_m)
    this.heightsRangeTarget.textContent = this.textValue.heights_range
      .replace("__LO__", lo.toFixed(1)).replace("__HI__", hi.toFixed(1))
      .replace("__LOFT__", Math.round(lo * 3.28084).toLocaleString()).replace("__HIFT__", Math.round(hi * 3.28084).toLocaleString())
    const step = Number(elev.contour_m)
    this.contourEveryTarget.textContent = this.textValue.contour_every.replace("__M__", String(step)).replace("__FT__", (step * 3.28084).toFixed(step < 1 ? 1 : 0))
    // Made to the map's own extent, the layers sit under its picture and move with it when it's nudged.
    const corners = elev.aligned ? flight.corners : elev.corners
    if (!corners || !this.map) return
    const opts = (pane, opacity) => ({ width: elev.width, height: elev.height, opacity, pane })
    if (this.reliefOnTarget.checked) this.reliefLayer = rotatedImage(L, elev.relief_url, corners, opts("droneHeights", this._reliefOpacity())).addTo(this.map)
    if (this.contoursOnTarget.checked) this.contoursLayer = rotatedImage(L, elev.contours_url, corners, opts("droneContours", 1)).addTo(this.map)
    if (this.waterOnTarget.checked) this.waterLayer = rotatedImage(L, elev.water_url, corners, opts("droneWater", 0.9)).addTo(this.map)
  }

  toggleHeights(event) {
    this._drawHeights()
    this._savePrefs()
    const layer = event?.params?.layer
    if (layer) track("Drone Heights Layer Toggled", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, layer, on: event.target.checked })
  }

  _reliefOpacity() {
    return this.hasReliefOpacityTarget ? Math.min(1, Math.max(0.2, Number(this.reliefOpacityTarget.value) || 0.75)) : 0.75
  }

  setReliefOpacity() {
    this.reliefLayer?.setOpacity(this._reliefOpacity())
    if (this.hasReliefOpacityValueTarget) this.reliefOpacityValueTarget.textContent = `${Math.round(this._reliefOpacity() * 100)}%`
    this._savePrefs()
  }

  // For a flight stitched before the heights existed, or one that failed: ask the server to make them.
  async makeHeights() {
    const flight = this._base()
    if (!flight) return
    this.heightsMakeTarget.disabled = true
    try {
      const data = await this._send("POST", `${this.urlValue}/${flight.id}/elevation`, {})
      this._upsertFlight(data.drone_flight)
      this._drawHeights()
    } catch (error) {
      showToast(error.message || this.textValue.no_heights, "error")
    } finally {
      this.heightsMakeTarget.disabled = false
    }
  }

  // Phase 3: the flight in 3D, in its own window (drone-terrain) with the
  // heights file, the picture and the ground's size in metres. A flight whose
  // heights were made before the 3D view existed has them made again first
  // (a few seconds), then the window opens by itself.
  open3d() {
    const flight = this._base()
    const elev = flight?.elevation
    if (!flight || !elev || elev.status !== "ready" || !this.hasTerrainTarget) return
    if (!elev.heights_url) {
      if (this.open3dTried === flight.id) return showToast(this.textValue.no_heights, "error")
      this.open3dTried = flight.id
      this.open3dPending = flight.id
      showToast(this.textValue.three_d_making)
      return this.makeHeights()
    }
    const terrain = this.application.getControllerForElementAndIdentifier(this.terrainTarget, "drone-terrain")
    const corners = elev.aligned ? flight.corners : elev.corners
    if (!terrain || !corners) return
    const metres = (p, q) => {
      const rad = Math.PI / 180
      const dLat = (q[0] - p[0]) * rad
      const dLon = (q[1] - p[1]) * rad * Math.cos(((p[0] + q[0]) / 2) * rad)
      return Math.hypot(dLat, dLon) * 6371000
    }
    const what = [this._day(flight), flight.label].filter(Boolean).join(" · ")
    terrain.open({
      title: this.textValue.three_d_title.replace("__WHAT__", what),
      heightsUrl: elev.heights_url,
      textureUrl: elev.aligned && flight.image_url ? flight.image_url : elev.relief_url,
      reliefUrl: elev.relief_url,
      lo: Number(elev.min_m), hi: Number(elev.max_m),
      widthM: metres(corners[0], corners[1]), heightM: metres(corners[0], corners[3])
    })
    track("Drone 3D Viewed", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, drone_flight_id: flight.id })
  }

  // While the heights are being made: asked every 15 s, for ten minutes at most.
  _watchHeights(id) {
    if (this.heightsWatch?.id === id) return
    clearTimeout(this.heightsWatch?.timer)
    const tick = async () => {
      if (!this.heightsWatch || this.heightsWatch.id !== id) return
      try {
        const data = await this._send("GET", `${this.urlValue}/${id}`)
        this._upsertFlight(data.drone_flight)
        if (data.drone_flight.elevation?.status !== "making") {
          this.heightsWatch = null
          if (this._base()?.id === id) this._drawHeights()
          this._reloadPiles()
          if (this.open3dPending === id) {
            this.open3dPending = null
            if (this._base()?.id === id && data.drone_flight.elevation?.status === "ready") this.open3d()
          }
          return
        }
      } catch {
        // asked again below
      }
      if (++this.heightsWatch.tries < 40) this.heightsWatch.timer = setTimeout(tick, 15000)
    }
    this.heightsWatch = { id, tries: 0, timer: setTimeout(tick, 15000) }
  }

  // The volume inside the outline: the ground above and below the base at
  // its edge (a stockpile), and the cut and fill since the Before date
  // when one is picked. The figures sit on the shape.
  async _measureVolume(m) {
    const flight = this._base()
    const other = this._other()
    if (!flight?.elevation || flight.elevation.status !== "ready" || !m.shape) return
    m.shape.setTooltipContent(this.textValue.volume_working)
    try {
      const points = m.points.map((p) => [p.lat, p.lng])
      const data = await this._send("POST", `${this.urlValue}/${flight.id}/volume`, { points, other_id: other?.elevation?.status === "ready" ? other.id : null })
      const v = data.volume
      const lines = [
        this.textValue.volume_result.replace("__FILL__", this._m3(v.fill_m3)).replace("__CUT__", this._m3(v.cut_m3)),
        this.textValue.volume_area.replace("__AREA__", Math.round(v.area_m2).toLocaleString())
          .replace("__BASE__", v.base === "plane" ? this.textValue.volume_base_plane : this.textValue.volume_base_lowest)
      ]
      if (v.other && other) {
        lines.push(this.textValue.volume_since.replace("__DATE__", this._day(other))
          .replace("__FILL__", this._m3(v.other.fill_m3)).replace("__CUT__", this._m3(v.other.cut_m3)).replace("__NET__", this._m3(v.other.net_m3, true)))
      }
      const box = document.createElement("div")
      box.className = "text-left whitespace-nowrap"
      lines.forEach((text, i) => {
        const div = document.createElement("div")
        div.textContent = text
        if (i === 0) div.className = "font-semibold"
        box.append(div)
      })
      m.shape.setTooltipContent(box)
      this._offerPile(m, v)
    } catch (error) {
      m.shape.setTooltipContent(error.message || this.textValue.volume_failed)
    }
  }

  // ── Stockpiles (Larry 2026-10-27: "DroneDeploy gives you volume... we're measuring stockpiles") ──
  // A measured pile can be kept with a name and a material: it then shows in
  // tons and is measured again on every flight, so the list says what came
  // and went. The piles live on the project; each flight has its own figures.

  _offerPile(m, v) {
    if (!this.hasPileFormTarget || !v || !m.shape) return
    this.pileOffer = { shape: m.shape, points: m.points.map((p) => [p.lat, p.lng]), volume: v }
    this.pileFormNoteTarget.textContent = `· ${this._m3(v.fill_m3)}`
    this.pileNameTarget.value = this.textValue.pile_default.replace("__N__", String((this.stockpiles?.length || 0) + 1))
    this.pileMaterialChanged()
    this.pileFormTarget.classList.remove("hidden")
  }

  pileMaterialChanged() {
    const density = Number(this.pileMaterialTarget.selectedOptions[0]?.dataset.density)
    if (density) this.pileDensityTarget.value = String(Math.round(this._densityShown(density)))
    this.pileDensityUnitTarget.textContent = this.textValue.density_unit === "lb_yd3" ? "lb/yd³" : "kg/m³"
  }

  hidePileForm() {
    if (this.hasPileFormTarget) this.pileFormTarget.classList.add("hidden")
    this.pileOffer = null
  }

  async savePile() {
    const offer = this.pileOffer
    if (!offer) return
    const density = this._densityFromShown(Number(this.pileDensityTarget.value))
    if (!(density > 0)) return showToast(this.textValue.pile_weight_needed, "error")
    try {
      const data = await this._send("POST", this.pilesUrlValue, {
        name: this.pileNameTarget.value.trim() || this.textValue.pile_default.replace("__N__", "1"),
        material: this.pileMaterialTarget.value, density_t_m3: density, points: offer.points
      })
      this.stockpiles = [...(this.stockpiles || []).filter((p) => p.id !== data.stockpile.id), data.stockpile]
      offer.shape.remove()
      this.hidePileForm()
      if (this.hasMeasureClearTarget) this.measureClearTarget.classList.toggle("hidden", !this.measures || this.measures.getLayers().length === 0)
      this._drawPiles()
      showToast(this.textValue.pile_saved)
    } catch (error) {
      showToast(error.message || this.textValue.pile_failed, "error")
    }
  }

  // The table for the flight shown and every pile's outline on the map, with its tons.
  _drawPiles() {
    this.pileLayer?.remove()
    this.pileLayer = null
    if (!this.hasPilesPanelTarget) return
    const piles = this.stockpiles || []
    const flight = this._base()
    const show = piles.length > 0 && flight?.status === "ready"
    this.pilesPanelTarget.classList.toggle("hidden", !show)
    if (!show) return
    this.pilesTitleTarget.textContent = this.textValue.piles_title.replace("__DATE__", this._day(flight))
    const earlier = this.flights
      .filter((f) => f.status === "ready" && (f.flown_on < flight.flown_on || (f.flown_on === flight.flown_on && f.id < flight.id)))
      .sort((a, b) => (a.flown_on < b.flown_on ? 1 : a.flown_on > b.flown_on ? -1 : b.id - a.id))
    const measured = (pile, f) => {
      const m = pile.measures?.[String(f.id)]
      return m && m.fill_m3 != null ? m : null
    }
    const noHeights = flight.elevation?.status !== "ready"
    this.pilesRowsTarget.replaceChildren(...piles.map((pile) => {
      const now = measured(pile, flight)
      const before = earlier.map((f) => [f, measured(pile, f)]).find(([, m]) => m)
      const change = !now ? "" : before
        ? this.textValue.pile_change.replace("__TONS__", this._tons(pile, now.fill_m3 - before[1].fill_m3, true)).replace("__DATE__", this._day(before[0]))
        : this.textValue.piles_first
      const tr = document.createElement("tr")
      const cells = [
        pile.name, this.textValue.materials?.[pile.material] || pile.material,
        now ? this._m3(now.fill_m3) : noHeights ? this.textValue.piles_no_heights : this.textValue.piles_not_on_flight,
        now ? this._tons(pile, now.fill_m3) : "", change
      ]
      cells.forEach((text, i) => {
        const td = document.createElement("td")
        td.className = i >= 2 ? "py-1 pl-2 text-right tabular-nums" : "py-1 pr-2"
        td.textContent = text
        tr.append(td)
      })
      const td = document.createElement("td")
      td.className = "py-1 pl-2 text-right"
      const x = document.createElement("button")
      x.type = "button"
      x.className = "text-gray-400 hover:text-red-600 cursor-pointer"
      x.textContent = "×"
      x.title = this.textValue.pile_remove
      x.addEventListener("click", () => this.askRemovePile(pile.id))
      td.append(x)
      tr.append(td)
      return tr
    }))
    if (!this.map) return
    this.pileLayer = L.layerGroup(piles.map((pile) => {
      const shape = L.polygon(pile.points, { color: "#b45309", weight: 2, dashArray: "5 4", fillColor: "#f59e0b", fillOpacity: 0.1 })
      const now = measured(pile, flight)
      shape.bindTooltip(now ? `${pile.name} · ${this._tons(pile, now.fill_m3)}` : pile.name, { permanent: true, direction: "center", className: "drone-measure-label" })
      return shape
    })).addTo(this.map)
  }

  // Tons (US) for English, tonnes elsewhere: the material's loose weight times the volume.
  _tons(pile, m3, signed = false) {
    const t = (Number(m3) || 0) * Number(pile.density_t_m3 || 0) * (this.textValue.density_unit === "lb_yd3" ? 1.10231 : 1)
    const abs = Math.abs(t)
    const text = abs < 100 ? abs.toFixed(1) : Math.round(abs).toLocaleString()
    return `${signed ? (t < 0 ? "−" : "+") : ""}${text} ${this.textValue.ton_unit}`
  }

  // The weight field: lb per cubic yard for English, kg per cubic metre elsewhere; kept as tonnes per cubic metre.
  _densityShown(tPerM3) {
    return this.textValue.density_unit === "lb_yd3" ? tPerM3 * 1685.56 : tPerM3 * 1000
  }

  _densityFromShown(shown) {
    return this.textValue.density_unit === "lb_yd3" ? shown / 1685.56 : shown / 1000
  }

  askRemovePile(id) {
    const pile = (this.stockpiles || []).find((p) => p.id === id)
    if (!pile || !this.hasPileRemoveModalTarget) return
    this.pileToRemove = id
    this.pileRemoveNameTarget.textContent = pile.name
    this.pileRemoveModalTarget.classList.remove("hidden")
  }

  hidePileRemove() {
    this.pileRemoveModalTarget.classList.add("hidden")
    this.pileToRemove = null
  }

  async removePile() {
    const id = this.pileToRemove
    if (!id) return
    try {
      await this._send("DELETE", `${this.pilesUrlValue}/${id}`)
      this.stockpiles = (this.stockpiles || []).filter((p) => p.id !== id)
      this.hidePileRemove()
      this._drawPiles()
      showToast(this.textValue.pile_removed)
    } catch (error) {
      showToast(error.message || this.textValue.pile_failed, "error")
    }
  }

  // Once a flight's heights are made the server has measured every pile on it: ask for the list again.
  async _reloadPiles() {
    if (!this.hasPilesPanelTarget || !(this.stockpiles || []).length) return
    try {
      const data = await this._send("GET", this.urlValue)
      this.stockpiles = data.stockpiles || []
      this._drawPiles()
    } catch {
      // next time
    }
  }

  // Cubic metres and cubic yards; signed for a change.
  _m3(value, signed = false) {
    const n = Number(value) || 0
    const abs = Math.abs(n)
    const m3 = abs < 100 ? abs.toFixed(1) : Math.round(abs).toLocaleString()
    const yd3 = Math.round(abs * 1.30795).toLocaleString()
    return `${signed ? (n < 0 ? "−" : "+") : ""}${m3} m³ (${yd3} yd³)`
  }

  // ── Survey targets (phase 2) ────────────────────────────────────────
  // A survey CSV checked against the map's heights: a marker per target
  // with the difference, and the table under the map. Kept on the flight.
  pickTargets() {
    const flight = this._base()
    if (!flight?.elevation || flight.elevation.status !== "ready") return showToast(this.textValue.no_heights, "error")
    this.targetsFileTarget.value = ""
    this.targetsFileTarget.click()
  }

  async targetsChosen() {
    const file = this.targetsFileTarget.files?.[0]
    const flight = this._base()
    if (!file || !flight) return
    this.targetsButtonTarget.disabled = true
    this.targetsSummaryTarget.textContent = this.textValue.targets_checking
    this.targetsPanelTarget.classList.remove("hidden")
    try {
      const csv = await file.text()
      const data = await this._send("POST", `${this.urlValue}/${flight.id}/targets`, { csv })
      this._upsertFlight({ ...flight, survey_targets: data.survey_targets })
      this._drawTargets()
    } catch (error) {
      this.targetsPanelTarget.classList.toggle("hidden", !this._base()?.survey_targets)
      this._drawTargets()
      showToast(error.message || this.textValue.targets_failed, "error")
    } finally {
      this.targetsButtonTarget.disabled = false
    }
  }

  async clearTargets() {
    const flight = this._base()
    if (!flight) return
    try {
      await this._send("DELETE", `${this.urlValue}/${flight.id}/targets`)
      this._upsertFlight({ ...flight, survey_targets: null })
      this._drawTargets()
    } catch (error) {
      showToast(error.message || this.textValue.targets_failed, "error")
    }
  }

  _drawTargets() {
    this.targetLayer?.remove()
    this.targetLayer = null
    if (!this.hasTargetsPanelTarget) return
    const flight = this._base()
    const t = flight?.status === "ready" ? flight.survey_targets : null
    this.targetsPanelTarget.classList.toggle("hidden", !t)
    if (!t || !this.map) return
    const rows = t.rows || []
    const rms = t.rms_dz_m == null ? null : Number(t.rms_dz_m)
    this.targetsSummaryTarget.textContent = this.textValue.targets_summary
      .replace("__COUNT__", rows.length).replace("__ON__", t.on_map ?? 0)
      .replace("__CM__", rms == null ? "–" : (rms * 100).toFixed(1)).replace("__IN__", rms == null ? "–" : (rms * 39.3701).toFixed(1))
      .replace("__SYSTEM__", t.system || "")
    this.targetsRowsTarget.replaceChildren(...rows.map((r) => {
      const tr = document.createElement("tr")
      const cells = [r.name || "", this._mft(r.z_m), r.on_map ? this._mft(r.map_z_m) : this.textValue.targets_off, r.on_map ? this._cm(r.dz_m) : ""]
      cells.forEach((text, i) => {
        const td = document.createElement("td")
        td.className = i ? "py-1 text-right tabular-nums" : "py-1"
        td.textContent = text
        tr.append(td)
      })
      return tr
    }))
    this.targetLayer = L.layerGroup(rows.filter((r) => r.lat != null && r.lon != null).map((r) => {
      const marker = L.circleMarker([r.lat, r.lon], { radius: 7, color: r.on_map ? "#111827" : "#9ca3af", weight: 2, fillColor: "#ffffff", fillOpacity: 0.9 })
      marker.bindTooltip(`${r.name || ""}${r.on_map ? ` · ${this._cm(r.dz_m)}` : ""}`, { permanent: true, direction: "top", offset: [0, -6], className: "drone-measure-label" })
      return marker
    })).addTo(this.map)
  }

  _mft(m) {
    return m == null ? "" : `${Number(m).toFixed(2)} m · ${(Number(m) * 3.28084).toFixed(2)} ft`
  }

  _cm(dz) {
    const n = Number(dz) || 0
    return `${n < 0 ? "−" : "+"}${Math.abs(n * 100).toFixed(1)} cm`
  }

  // ── Measuring ───────────────────────────────────────────────────────
  // A distance or an area, DroneDeploy's bread and butter: click points,
  // double-click to finish; the figure sits on the shape. Not kept yet.
  measure(event) {
    const kind = event.params.kind
    if (this.measuring?.kind === kind) return this._endMeasure()
    if (this.nudge) this.cancelNudge()
    this._endMeasure()
    this.measuring = { kind, points: [] }
    this.measures ||= L.layerGroup().addTo(this.map)
    this.measureLineTarget.setAttribute("aria-pressed", String(kind === "line"))
    this.measureAreaTarget.setAttribute("aria-pressed", String(kind === "area"))
    if (this.hasMeasureVolumeTarget) this.measureVolumeTarget.setAttribute("aria-pressed", String(kind === "volume"))
    this.measureHintTarget.textContent = kind === "volume" ? this.textValue.volume_hint : this.textValue.measure_hint
    this.measureHintTarget.classList.remove("hidden")
    this.mapTarget.style.cursor = "crosshair"
    this.map?.doubleClickZoom.disable()
  }

  _endMeasure() {
    if (this.measuring?.points.length >= 2 && this.measuring.shape) this.measuring.shape.closePopup()
    // An outline for a volume: the server works it out once it's closed.
    if (this.measuring?.kind === "volume" && this.measuring.points.length >= 3 && this.measuring.shape) this._measureVolume(this.measuring)
    if (this.measuring && this.measuring.points.length < 2) this.measuring.shape?.remove()
    this.measuring = null
    if (this.hasMeasureLineTarget) this.measureLineTarget.setAttribute("aria-pressed", "false")
    if (this.hasMeasureAreaTarget) this.measureAreaTarget.setAttribute("aria-pressed", "false")
    if (this.hasMeasureVolumeTarget) this.measureVolumeTarget.setAttribute("aria-pressed", "false")
    if (this.hasMeasureHintTarget) this.measureHintTarget.classList.add("hidden")
    if (this.hasMeasureClearTarget) this.measureClearTarget.classList.toggle("hidden", !this.measures || this.measures.getLayers().length === 0)
    this.mapTarget.style.cursor = this.nudge ? "move" : ""
    this.map?.doubleClickZoom.enable()
  }

  _mapClick(event) {
    if (!this.measuring) return
    const m = this.measuring
    m.points.push(event.latlng)
    if (!m.shape) {
      m.shape = (m.kind === "volume"
        ? L.polygon(m.points, { color: "#d97706", weight: 2, fillOpacity: 0.15 })
        : m.kind === "area"
          ? L.polygon(m.points, { color: "#7c3aed", weight: 2, fillOpacity: 0.15 })
          : L.polyline(m.points, { color: "#7c3aed", weight: 3 })).addTo(this.measures)
      m.shape.bindTooltip("", { permanent: true, direction: "center", className: "drone-measure-label" })
    } else {
      m.shape.setLatLngs(m.points)
    }
    L.circleMarker(event.latlng, { radius: 4, color: "#ffffff", weight: 1.5, fillColor: m.kind === "volume" ? "#d97706" : "#7c3aed", fillOpacity: 1, interactive: false }).addTo(this.measures)
    m.shape.setTooltipContent(this._measureText(m))
  }

  _mapDoubleClick() {
    if (this.measuring) this._endMeasure()
  }

  clearMeasures() {
    this._endMeasure()
    this.measures?.clearLayers()
    if (this.hasMeasureClearTarget) this.measureClearTarget.classList.add("hidden")
  }

  // Metres and feet along the points, or square metres, square feet and
  // acres inside them (flat maths over a small site is plenty).
  _measureText(m) {
    const pts = m.points
    if (pts.length < 2) return ""
    const lat0 = pts[0].lat * Math.PI / 180
    const xy = pts.map((p) => [(p.lng - pts[0].lng) * 111320 * Math.cos(lat0), (p.lat - pts[0].lat) * 111132])
    if (m.kind === "line") {
      let metres = 0
      for (let i = 1; i < xy.length; i++) metres += Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1])
      return `${metres.toFixed(metres < 100 ? 1 : 0)} m · ${Math.round(metres * 3.28084)} ft`
    }
    if (pts.length < 3) return ""
    let twice = 0
    for (let i = 0; i < xy.length; i++) {
      const [x1, y1] = xy[i]
      const [x2, y2] = xy[(i + 1) % xy.length]
      twice += x1 * y2 - x2 * y1
    }
    const sqm = Math.abs(twice) / 2
    const sqft = sqm * 10.7639
    const acres = sqft / 43560
    return `${Math.round(sqm).toLocaleString()} m² · ${Math.round(sqft).toLocaleString()} ft²${acres >= 0.1 ? ` · ${acres.toFixed(2)} ac` : ""}`
  }

  // ── The dates in a line ─────────────────────────────────────────────
  // A dot per flight on the ground, spaced by when it was flown; the knob
  // sits on the flight shown. Drag it and the aerial of the dot behind
  // fades into the one ahead (Phelipe 2026-10-22: "it transitions from one
  // aerial to another and shows with a dot the dates of each one").

  _timelineFlights() {
    return this._earlierFirst(this._shown())
  }

  // 0..1 along the line for a flight (by time; one flight sits in the middle).
  _timelineAt(flight, list = this._timelineFlights()) {
    if (list.length < 2) return 0.5
    const first = this._date(list[0]).getTime()
    const last = this._date(list[list.length - 1]).getTime()
    if (last <= first) return list.indexOf(flight) / (list.length - 1)
    return (this._date(flight).getTime() - first) / (last - first)
  }

  _paintTimeline() {
    if (!this.hasTimelineTarget) return
    const list = this._timelineFlights()
    this.timelineTarget.classList.toggle("hidden", list.length < 2)
    if (list.length < 2) return
    const dots = list.map((flight) => {
      const at = this._timelineAt(flight, list)
      const dot = document.createElement("button")
      dot.type = "button"
      dot.className = "absolute -translate-x-1/2 flex flex-col items-center cursor-pointer group"
      dot.style.left = `${at * 100}%`
      dot.style.top = "9px"
      dot.title = flight.label || ""
      const circle = document.createElement("span")
      circle.className = "w-3.5 h-3.5 rounded-full border-2 border-white shadow " + (this.picks.includes(flight.id) ? "bg-brand" : "bg-gray-400 group-hover:bg-gray-500")
      const label = document.createElement("span")
      label.className = "mt-1 text-[13px] whitespace-nowrap " + (this.picks.includes(flight.id) ? "text-brand font-semibold" : "text-gray-500")
      label.textContent = this._day(flight)
      dot.append(circle, label)
      dot.addEventListener("click", (event) => { event.stopPropagation(); this._pick(flight) })
      return dot
    })
    this.timelineDotsTarget.replaceChildren(...dots)
    const knobAt = this.scrub ? this.scrub.at : this._timelineAt(this._base() || list[list.length - 1], list)
    this.timelineKnobTarget.style.left = `${knobAt * 100}%`
    this.timelineFillTarget.style.width = `${knobAt * 100}%`
  }

  timelineDown(event) {
    const list = this._timelineFlights()
    if (list.length < 2) return
    event.preventDefault()
    if (this.nudge) this.cancelNudge()
    const box = this.timelineTrackTarget.getBoundingClientRect()
    const move = (e) => this._scrubTo(Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)), list)
    const up = (e) => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", up)
      document.removeEventListener("pointercancel", up)
      this._scrubTo(Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)), list, true)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up)
    document.addEventListener("pointercancel", up)
    move(event)
  }

  // The two dots around the knob and how far between them it sits. On a dot
  // (or let go within a whisker of one) that flight is simply picked.
  _scrubTo(at, list, done = false) {
    let before = list[0]
    let after = list[list.length - 1]
    list.forEach((flight) => {
      const t = this._timelineAt(flight, list)
      if (t <= at) before = flight
    })
    after = list.find((flight) => this._timelineAt(flight, list) >= at) || after
    const t0 = this._timelineAt(before, list)
    const t1 = this._timelineAt(after, list)
    const f = t1 > t0 ? (at - t0) / (t1 - t0) : 0
    const snap = f < 0.06 || before === after ? before : f > 0.94 ? after : null
    if (snap) {
      this.scrub = null
      if (!(this.picks.length === 1 && this.picks[0] === snap.id)) {
        this.picks = [snap.id]
        this._paintChips()
        this._paintAi()
        this._draw(false)
      }
      this.scrub = { at: this._timelineAt(snap, list) }
      this._paintTimeline()
      this.scrub = null
      return
    }
    this.scrub = { at, before, after, f }
    if (!(this.picks.length === 1 && this.picks[0] === before.id)) {
      this.picks = [before.id]
      this._paintChips()
      this._paintAi()
      this._draw(false)
    }
    this._drawScrub()
    this._paintTimeline()
    if (done) track("Drone Flights Timeline Scrubbed", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue })
  }

  // The flight ahead of the knob laid over the one behind, as see-through as
  // the knob is far from it.
  _drawScrub() {
    if (!this.map) return
    this.scrubLayer?.remove()
    this.scrubLayer = null
    const s = this.scrub
    if (!s?.after || s.after === s.before) return
    this.scrubLayer = rotatedImage(L, s.after.image_url, s.after.corners, { width: s.after.width, height: s.after.height, opacity: s.f, pane: "droneScrub" }).addTo(this.map)
  }

  // ── Nudge ───────────────────────────────────────────────────────────
  // The aerial a few feet off: drag it over the satellite, turn or resize
  // it a touch, Save. Worked out in metres east and north of its middle.

  startNudge() {
    const flight = this._base()
    if (!this.map || !flight?.corners || this.nudge) return
    this._endMeasure()
    this.scrub = null
    this._drawScrub()
    this.nudge = { flight, corners: flight.corners.map((c) => [...c]), was: flight.corners, history: [], future: [] }
    this.nudgeBarTarget.classList.remove("hidden")
    this._paintMoveTools()
    this._paintNudgeSteps()
    this.map.dragging.disable()
    this.mapTarget.style.cursor = "move"
    this._nudgeDown = (event) => {
      if (!this.nudge || event.button) return
      event.preventDefault()
      const before = this.nudge.corners.map((c) => [...c])
      let last = this.map.mouseEventToLatLng(event)
      let moved = false
      const move = (e) => {
        const here = this.map.mouseEventToLatLng(e)
        this._nudgeBy({ dLat: here.lat - last.lat, dLon: here.lng - last.lng }, false)
        last = here
        moved = true
      }
      const up = () => {
        document.removeEventListener("pointermove", move)
        document.removeEventListener("pointerup", up)
        if (moved) this._nudgeStep(before)
      }
      document.addEventListener("pointermove", move)
      document.addEventListener("pointerup", up)
    }
    this.mapTarget.addEventListener("pointerdown", this._nudgeDown)
  }

  nudgeTurn(event) {
    this._nudgeBy({ turn: Number(event.params.turn) || 0 })
  }

  nudgeScale(event) {
    this._nudgeBy({ scale: Number(event.params.scale) || 1 })
  }

  // Each step (a drag, a turn, a resize) is kept so it can be undone and redone.
  _nudgeStep(before) {
    this.nudge.history.push(before)
    this.nudge.future = []
    this._paintNudgeSteps()
  }

  nudgeUndo() {
    if (!this.nudge?.history.length) return
    this.nudge.future.push(this.nudge.corners.map((c) => [...c]))
    this.nudge.corners = this.nudge.history.pop()
    this.baseLayer?.setCorners(this.nudge.corners)
    this._paintNudgeSteps()
  }

  nudgeRedo() {
    if (!this.nudge?.future.length) return
    this.nudge.history.push(this.nudge.corners.map((c) => [...c]))
    this.nudge.corners = this.nudge.future.pop()
    this.baseLayer?.setCorners(this.nudge.corners)
    this._paintNudgeSteps()
  }

  _paintNudgeSteps() {
    if (this.hasNudgeUndoTarget) this.nudgeUndoTarget.disabled = !this.nudge?.history.length
    if (this.hasNudgeRedoTarget) this.nudgeRedoTarget.disabled = !this.nudge?.future.length
  }

  // Shift, turn (degrees clockwise) or resize the corners about their middle.
  _nudgeBy({ dLat = 0, dLon = 0, turn = 0, scale = 1 }, step = true) {
    if (!this.nudge) return
    if (step) this._nudgeStep(this.nudge.corners.map((c) => [...c]))
    const corners = this.nudge.corners
    const cLat = corners.reduce((sum, c) => sum + c[0], 0) / 4
    const cLon = corners.reduce((sum, c) => sum + c[1], 0) / 4
    const k = Math.cos(cLat * Math.PI / 180)
    const rad = turn * Math.PI / 180
    this.nudge.corners = corners.map(([lat, lon]) => {
      let e = (lon - cLon) * 111320 * k
      let n = (lat - cLat) * 111132
      const e2 = (e * Math.cos(rad) + n * Math.sin(rad)) * scale
      const n2 = (-e * Math.sin(rad) + n * Math.cos(rad)) * scale
      return [cLat + dLat + n2 / 111132, cLon + dLon + e2 / (111320 * k)]
    })
    this.baseLayer?.setCorners(this.nudge.corners)
  }

  async saveNudge() {
    if (!this.nudge) return
    const { flight, corners } = this.nudge
    this.nudgeSaveTarget.disabled = true
    try {
      const data = await this._send("PATCH", `${this.urlValue}/${flight.id}`, { corners: corners.map((c) => c.map((v) => Number(v.toFixed(8)))) })
      this.flights = this.flights.map((f) => (f.id === data.drone_flight.id ? { ...f, ...data.drone_flight } : f))
      this.undone?.delete(flight.id)
      this._endNudge()
      showToast(this.textValue.nudge_saved)
      this._draw(false)
    } catch (error) {
      showToast(error.message || this.textValue.nudge_failed, "error")
      this.nudgeSaveTarget.disabled = false
    }
  }

  cancelNudge() {
    if (!this.nudge) return
    this.baseLayer?.setCorners(this.nudge.was)
    this._endNudge()
    this._draw(false)
  }

  _endNudge() {
    this.mapTarget.removeEventListener("pointerdown", this._nudgeDown)
    this._nudgeDown = null
    this.nudge = null
    this.nudgeBarTarget.classList.add("hidden")
    this.nudgeSaveTarget.disabled = false
    this.mapTarget.style.cursor = ""
    this.map?.dragging.enable()
    this._paintMoveTools()
  }

  _drawPlan() {
    this.planLayer?.remove()
    this.planLayer = null
    if (!this.map || !this.plan?.corners || !this.plan.image_url || !this.plansOnTarget.checked) return
    this.planLayer = rotatedImage(L, this.plan.image_url, this.plan.corners, {
      width: this.plan.width, height: this.plan.height, opacity: Number(this.opacityTarget.value) || 0.7, pane: "sitePlan"
    }).addTo(this.map)
  }

  togglePlan() {
    this._drawPlan()
    this._savePrefs()
  }

  // The aerial's own see-through (Phelipe 2026-10-22): the flight shown, and
  // the Before date when two are picked. Looking through it at the satellite
  // is how you tell whether it sits right, before a Nudge.
  _aerialOpacity() {
    return this.hasAerialOpacityTarget ? Math.min(1, Math.max(0.1, Number(this.aerialOpacityTarget.value) || 1)) : 1
  }

  _beforeOpacity() {
    return this.hasBeforeOpacityTarget ? Math.min(1, Math.max(0.1, Number(this.beforeOpacityTarget.value) || 1)) : 1
  }

  setAerialOpacity() {
    this.baseLayer?.setOpacity(this._aerialOpacity())
    if (this.hasAerialOpacityValueTarget) this.aerialOpacityValueTarget.textContent = `${Math.round(this._aerialOpacity() * 100)}%`
    this._savePrefs()
  }

  setBeforeOpacity() {
    this.compareLayer?.setOpacity(this._beforeOpacity())
    if (this.hasBeforeOpacityValueTarget) this.beforeOpacityValueTarget.textContent = `${Math.round(this._beforeOpacity() * 100)}%`
  }

  // A lock on the flight (Phelipe 2026-10-22: "I accidentally moved one out
  // of place... the user clicks on the lock to lock or unlock it, and when
  // they unlock it the nudge option opens up"). Locked until clicked.
  toggleLock() {
    this.unlocked = !this.unlocked
    if (!this.unlocked && this.nudge) this.cancelNudge()
    this._paintMoveTools()
  }

  _paintMoveTools() {
    const flight = this._base()
    const shown = flight && flight.status === "ready" && flight.corners && flight.image_url
    const allowed = Boolean(this.unlocked)
    if (this.hasLockTarget) {
      this.lockTarget.setAttribute("aria-pressed", String(allowed))
      this.lockClosedTarget.classList.toggle("hidden", allowed)
      this.lockOpenTarget.classList.toggle("hidden", !allowed)
      this.lockTextTarget.textContent = allowed ? this.textValue.unlocked : this.textValue.locked
      this.lockTarget.classList.toggle("hidden", !shown)
    }
    if (this.hasMoveToolsTarget) this.moveToolsTarget.classList.toggle("hidden", !(shown && allowed))
    if (this.hasNudgeLinkTarget) this.nudgeLinkTarget.disabled = !(shown && allowed && !this.nudge)
    if (this.hasPinLinkTarget) this.pinLinkTarget.disabled = !(shown && allowed && !this.nudge)
    if (this.hasUndoNudgeLinkTarget) {
      this.undoNudgeLinkTarget.classList.toggle("hidden", !flight?.can_undo_nudge)
      this.undoNudgeLinkTarget.disabled = !(allowed && !this.nudge)
      this.undoNudgeTextTarget.textContent = this.undone?.has(flight?.id) ? this.textValue.redo_move : this.textValue.undo_move
    }
  }

  // Undo the last saved move (the server swaps it with where it was, so
  // the same button then redoes it).
  async undoNudge() {
    const flight = this._base()
    if (!flight?.can_undo_nudge) return
    this.undoNudgeLinkTarget.disabled = true
    try {
      const data = await this._send("PATCH", `${this.urlValue}/${flight.id}`, { undo_nudge: true })
      this.flights = this.flights.map((f) => (f.id === data.drone_flight.id ? { ...f, ...data.drone_flight } : f))
      this.undone ||= new Set()
      if (this.undone.has(flight.id)) this.undone.delete(flight.id)
      else this.undone.add(flight.id)
      showToast(this.textValue.move_undone)
      this._draw(false)
    } catch (error) {
      showToast(error.message || this.textValue.nudge_failed, "error")
      this._paintMoveTools()
    }
  }

  setOpacity() {
    this.planLayer?.setOpacity(Number(this.opacityTarget.value))
    if (this.hasPlanOpacityValueTarget) this.planOpacityValueTarget.textContent = `${Math.round(Number(this.opacityTarget.value) * 100)}%`
    this._savePrefs()
  }

  // The walks' routes, dashed, with the walk's name and day on hover.
  _drawRoutes() {
    this.routeLayer?.remove()
    this.routeLayer = null
    if (!this.map || !this.routesOnTarget.checked || !this.walks.length) return
    this.routeLayer = L.layerGroup(this.walks.flatMap((walk) => {
      if (!Array.isArray(walk.route) || walk.route.length < 2) return []
      const edge = L.polyline(walk.route, { color: "#ffffff", weight: 5, opacity: 0.7, interactive: false })
      const line = L.polyline(walk.route, { color: ROUTE_COLOR, weight: 2.5, opacity: 1, dashArray: "6 6" })
      line.bindTooltip(`${walk.day_label} · ${walk.name}`, { sticky: true })
      return [edge, line]
    })).addTo(this.map)
  }

  toggleRoutes() {
    this._drawRoutes()
    this._savePrefs()
  }

  // The earlier of two picked dates, on the left of the line.
  _drawCompare() {
    if (!this.map) return
    const other = this._other()
    const base = this._base()
    this.compareLayer?.remove()
    this.compareLayer = null
    const on = other && other.status === "ready" && other.corners && other.image_url && !this.scrub
    this.sliderTarget.classList.toggle("hidden", !on)
    if (this.hasBeforeOpacityRowTarget) this.beforeOpacityRowTarget.classList.toggle("hidden", !on)
    const pane = this.map.getPane("droneCompare")
    if (!on) {
      if (pane) pane.style.clipPath = ""
      return
    }
    this.compareLayer = rotatedImage(L, other.image_url, other.corners, { width: other.width, height: other.height, opacity: this._beforeOpacity(), pane: "droneCompare" }).addTo(this.map)
    this.leftLabelTarget.textContent = this._day(other)
    this.rightLabelTarget.textContent = base ? this._day(base) : ""
    this._clip()
  }

  // Only the part of the earlier map left of the line shows. The pane moves
  // with the map, so the cut is worked out in its own pixels.
  _clip() {
    if (!this.map || !this.compareLayer) return
    const pane = this.map.getPane("droneCompare")
    const x = this.split * this.mapTarget.clientWidth - L.DomUtil.getPosition(this.map.getPane("mapPane")).x
    pane.style.clipPath = `polygon(-100000px -100000px, ${x}px -100000px, ${x}px 100000px, -100000px 100000px)`
  }

  sliderDown(event) {
    event.preventDefault()
    const box = this.mapTarget.getBoundingClientRect()
    const move = (e) => {
      this.split = Math.min(0.95, Math.max(0.05, (e.clientX - box.left) / box.width))
      this.sliderTarget.style.left = `${this.split * 100}%`
      this._clip()
    }
    const up = () => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", up)
      document.removeEventListener("pointercancel", up)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up)
    document.addEventListener("pointercancel", up)
  }

  // ── What changed, said by the AI ────────────────────────────────────
  // With two dates picked: the button, and the answer kept for that pair
  // (asked once; "Ask again" asks afresh). Polls while the AI is at it.

  _pairKey() {
    const other = this._other()
    const base = this._base()
    return other && base ? `${other.id}-${base.id}` : null
  }

  async _paintAi() {
    const key = this._pairKey()
    const on = this.aiValue && key
    if (this.hasAiButtonTarget) this.aiButtonTarget.classList.toggle("hidden", !on)
    if (!on) {
      this._stopAiPolling()
      if (this.hasAiPanelTarget) this.aiPanelTarget.classList.add("hidden")
      return
    }
    if (!(key in this.comparisons)) {
      this.comparisons[key] = null
      try {
        const data = await this._send("GET", `${this.urlValue}/compare?from_id=${this._other().id}&to_id=${this._base().id}`)
        this.comparisons[key] = data.comparison || null
      } catch (_) { /* nothing kept yet */ }
      if (this._pairKey() !== key) return
    }
    this._showAi(this.comparisons[key])
  }

  _showAi(comparison) {
    if (!this.hasAiPanelTarget) return
    const other = this._other()
    const base = this._base()
    if (!comparison || !other || !base) {
      this._stopAiPolling()
      this.aiPanelTarget.classList.add("hidden")
      if (this.hasAiButtonTarget) this.aiButtonTarget.disabled = false
      return
    }
    this.aiPanelTarget.classList.remove("hidden")
    this.aiTitleTarget.textContent = this.textValue.ai_title.replace("__FROM__", this._day(other)).replace("__TO__", this._day(base))
    this.aiAgainTarget.hidden = comparison.status === "pending"
    if (this.hasAiButtonTarget) this.aiButtonTarget.disabled = comparison.status === "pending"
    this.aiListTarget.replaceChildren(...(comparison.changes || []).map((sentence) => {
      const li = document.createElement("li")
      li.textContent = sentence
      return li
    }))
    if (comparison.status === "pending") {
      this.aiStatusTarget.textContent = this.textValue.ai_working
      this._startAiPolling()
    } else if (comparison.status === "failed") {
      this.aiStatusTarget.textContent = this.textValue.ai_failed
      this._stopAiPolling()
    } else {
      this.aiStatusTarget.textContent = (comparison.changes || []).length ? "" : this.textValue.ai_none
      this._stopAiPolling()
    }
    this.aiStatusTarget.classList.toggle("hidden", !this.aiStatusTarget.textContent)
  }

  askAi() {
    this._ask(false)
  }

  askAgain() {
    this._ask(true)
  }

  async _ask(again) {
    const other = this._other()
    const base = this._base()
    if (!other || !base) return
    const key = this._pairKey()
    if (this.hasAiButtonTarget) this.aiButtonTarget.disabled = true
    try {
      const data = await this._send("POST", `${this.urlValue}/compare`, { from_id: other.id, to_id: base.id, again: again || undefined })
      this.comparisons[key] = data.comparison
      if (this._pairKey() === key) this._showAi(data.comparison)
    } catch (error) {
      showToast(error.message || this.textValue.ai_failed, "error")
      if (this.hasAiButtonTarget) this.aiButtonTarget.disabled = false
    }
  }

  _startAiPolling() {
    if (this._aiPolling) return
    this._aiPolls = 0
    this._aiPolling = setInterval(async () => {
      const key = this._pairKey()
      const comparison = key && this.comparisons[key]
      if (!comparison || comparison.status !== "pending" || ++this._aiPolls > AI_POLL_MAX) return this._stopAiPolling()
      try {
        const data = await this._send("GET", `${this.urlValue}/compare?from_id=${comparison.from_id}&to_id=${comparison.to_id}`)
        if (!data.comparison || data.comparison.status === "pending") return
        this.comparisons[key] = data.comparison
        if (this._pairKey() === key) this._showAi(data.comparison)
      } catch (_) { /* ask again next time */ }
    }, AI_POLL_MS)
  }

  _stopAiPolling() {
    if (this._aiPolling) clearInterval(this._aiPolling)
    this._aiPolling = null
  }

  // ── Line a map up by hand (no GPS in the file) ───────────────────────
  // The Site plan pop-up does the matching spots; it saves to this flight.
  // Pin to surveyed marks (Kloepfer K13-35, Quentin's marks on the ground): the
  // same pop-up on a flight that is already on the map, with the marks from
  // its uploaded survey list to pick from; a mark's coordinates can be typed
  // too. 2-3 marks move, turn and resize the map onto them, the height layers
  // with it; later flights line up with this one by themselves.
  lineUp({ pin = false } = {}) {
    const flight = this._base()
    if (!flight) return
    const marks = (flight.survey_targets?.rows || []).filter((r) => Number.isFinite(+r.lat) && Number.isFinite(+r.lon))
      .map((r, i) => ({ name: r.name || `#${i + 1}`, lat: +r.lat, lon: +r.lon }))
    this.dispatch("open", { target: window, prefix: "site-plan", detail: {
      mode: "map",
      lineUp: { url: `${this.urlValue}/${flight.id}`, marks, pin, plan: { id: flight.id, label: flight.label || this._day(flight), image_url: flight.image_url,
        width: flight.width, height: flight.height, corners: flight.corners, calibration: pin ? [] : flight.calibration, opacity: 0.8 } }
    } })
  }

  pinToMarks() {
    if (this.nudge) this.cancelNudge()
    this.lineUp({ pin: true })
  }

  planSaved(event) {
    const { plan, drone_flight: flight } = event.detail || {}
    if (flight) {
      this.flights = this.flights.map((f) => (f.id === flight.id ? { ...f, ...flight } : f))
      this.picks = [flight.id]
      this._paintChips()
      this._draw(true)
      this._paintAi()
    } else if (plan?.corners && this.loaded) {
      this.plan = plan
      this._paint()
    }
  }

  // ── Add drone flight ────────────────────────────────────────────────

  openAdd() {
    // Photos still going up (or paused): the window shows them, not a fresh form.
    if (this.upload) {
      this.addModalTarget.classList.remove("hidden")
      return
    }
    this._addError(null)
    this._clearFile()
    this.labelTarget.value = ""
    this.addModalTarget.classList.remove("hidden")
  }

  // Closes while a photo set goes up too: the upload keeps going behind the
  // page and the flight's card says how far it is (openAdd shows it again).
  closeAdd() {
    if (this._sending && !this.upload) return
    this.addModalTarget.classList.add("hidden")
  }

  // The drop box works like the Walkabout one: click to browse, or drop the
  // file on it (it turns blue while a file is over it). A finished map is
  // one file; the drone's photos come as a folder (dropped, or picked with
  // the photos link) or as one zip.
  pickFile() {
    if (this._sending) return
    this.fileTarget.click()
  }

  pickFolder(event) {
    event.stopPropagation()
    if (this._sending) return
    this.folderTarget.click()
  }

  fileChosen() {
    this._takeFiles(Array.from(this.fileTarget.files || []))
  }

  folderChosen() {
    this._takeFiles(Array.from(this.folderTarget.files || []), { folder: true })
  }

  dragOver(event) {
    event.preventDefault()
    if (!this._sending) this.dropzoneTarget.classList.add("border-blue-400", "bg-blue-50")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-blue-400", "bg-blue-50")
  }

  async drop(event) {
    event.preventDefault()
    this.dragLeave()
    if (this._sending) return
    const entries = Array.from(event.dataTransfer?.items || []).map((item) => item.webkitGetAsEntry?.()).filter(Boolean)
    if (entries.some((entry) => entry.isDirectory)) {
      // A folder: read it through (the SD card's DCIM folder has its photos a level or two down).
      const files = await this._filesIn(entries)
      this._takeFiles(files, { folder: true, name: entries.find((entry) => entry.isDirectory)?.name })
    } else {
      this._takeFiles(Array.from(event.dataTransfer?.files || []))
    }
  }

  // Every file under the dropped entries, each remembering its folder.
  async _filesIn(entries, into = [], depth = 0) {
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise((resolve) => entry.file(resolve, () => resolve(null)))
        if (file) {
          file._path = entry.fullPath
          into.push(file)
        }
      } else if (entry.isDirectory && depth < 6) {
        const reader = entry.createReader()
        let batch
        do {
          batch = await new Promise((resolve) => reader.readEntries(resolve, () => resolve([])))
          await this._filesIn(batch, into, depth + 1)
        } while (batch.length)
      }
      if (into.length > PHOTO_CAP * 2) break
    }
    return into
  }

  // What was dropped or picked: one map file, one zip, or the photos.
  _takeFiles(files, { folder = false, name = null } = {}) {
    if (!files.length) return
    this._addError(null)
    if (files.length === 1 && !folder) {
      return /\.zip$/i.test(files[0].name) ? this._takeZip(files[0]) : this._takeFile(files[0])
    }
    const photos = files.filter((f) => /\.jpe?g$/i.test(f.name) && !f.name.startsWith(".") && !/__MACOSX/.test(f.webkitRelativePath || f._path || ""))
    if (!photos.length) return this._addError(this.textValue.no_photos)
    if (photos.length > PHOTO_CAP) {
      return this._addError(this.textValue.photo_cap.replace("__COUNT__", photos.length.toLocaleString()).replace("__CAP__", PHOTO_CAP.toLocaleString()))
    }
    if (photos.some((f) => f.size > PHOTO_MAX_BYTES)) return this._addError(this.textValue.photo_type)
    const bytes = photos.reduce((sum, f) => sum + f.size, 0)
    this.file = null
    this.zipFile = null
    this.photoSet = { files: photos, folder: name || this._folderNameOf(photos), bytes }
    this._emailRow(true)
    this._setChip(`${this.photoSet.folder ? `${this.photoSet.folder} · ` : ""}${this.textValue.photos_chip.replace("__COUNT__", photos.length.toLocaleString())} · ${this._size(bytes)}`)
    this.addButtonTarget.disabled = false
    this._findResume()
  }

  _takeZip(file) {
    if (file.size > ZIP_MAX_BYTES) return this._addError(this.textValue.zip_too_big)
    this.file = null
    this.photoSet = null
    this.zipFile = file
    this._emailRow(true)
    this._setChip(`${file.name} · ${this._size(file.size)}`)
    this.addButtonTarget.disabled = false
    this._findResume()
  }

  _folderNameOf(files) {
    const path = files[0]?.webkitRelativePath || files[0]?._path || ""
    return path.split("/").filter(Boolean).slice(0, -1).pop() || ""
  }

  _takeFile(file) {
    if (!file) return
    this._addError(null)
    if (!/\.(tiff?|jpe?g|png|webp)$/i.test(file.name)) return this._addError(this.textValue.file_type)
    if (file.size > UPLOAD_LIMIT_BYTES) {
      return showSizeLimitModal(file, this.textValue.size_limit, { source: "drone_flights", project_id: this.projectIdValue, project_phase: this.projectPhaseValue })
    }
    this.photoSet = null
    this.zipFile = null
    this.resume = null
    this.resumeNoteTarget.classList.add("hidden")
    this.file = file
    this._emailRow(false)
    this._chip("ready")
    this.addButtonTarget.disabled = false
  }

  _clearFile() {
    this.file = null
    this.photoSet = null
    this.zipFile = null
    this.resume = null
    this._emailRow(false)
    this.fileTarget.value = ""
    this.folderTarget.value = ""
    this.fileChipsTarget.replaceChildren()
    this.fileChipsTarget.hidden = true
    this.progressTarget.classList.add("hidden")
    this.resumeNoteTarget.classList.add("hidden")
    this.flownOnTarget.disabled = false
    this.labelTarget.disabled = false
    this.addButtonTarget.disabled = true
  }

  // The gray chip for a photo set or a zip (× takes it back).
  _setChip(text) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded-lg text-[13px] bg-gray-100 text-gray-700"
    chip.innerHTML = '<svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"/></svg>'
    const label = document.createElement("span")
    label.className = "truncate"
    label.textContent = text
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
    remove.setAttribute("aria-label", "Remove file")
    remove.textContent = "×"
    remove.addEventListener("click", () => this._clearFile())
    chip.append(label, remove)
    this.fileChipsTarget.replaceChildren(chip)
    this.fileChipsTarget.hidden = false
  }

  // A flight of this project still waiting for these same photos (the same
  // names and sizes, or the same zip): the drop continues it instead of
  // starting another. The server says what it still needs.
  async _findResume() {
    this.resume = null
    this.resumeNoteTarget.classList.add("hidden")
    this.flownOnTarget.disabled = false
    this.labelTarget.disabled = false
    if (!this.loaded) {
      try { await this._load() } catch (_) { return }
    }
    const candidates = this.flights.filter((f) => f.status === "uploading" && f.source === "photos" && f.kind === (this.zipFile ? "zip" : "folder"))
    for (const flight of candidates) {
      let state
      try { state = (await this._send("GET", `${this.urlValue}/${flight.id}?upload=1`)).upload } catch (_) { continue }
      if (!state) continue
      if (this.zipFile) {
        if (state.filename !== this.zipFile.name || state.size !== this.zipFile.size) continue
        this.resume = { flight, upload: state }
      } else {
        const have = new Map(this.photoSet.files.map((f) => [f.name, f.size]))
        const pending = state.pending.filter((p) => have.get(p.name) === p.size)
        if (!pending.length) continue
        this.resume = { flight, upload: state, pending: new Set(pending.map((p) => p.name)) }
      }
      break
    }
    if (!this.resume) return
    const left = this.zipFile ? Math.max(0, this.resume.upload.part_count - this.resume.upload.uploaded_parts.length) : this.resume.pending.size
    this.resumeNoteTarget.textContent = this.textValue.resume_note.replace("__DATE__", this._date(this.resume.flight)).replace("__COUNT__", left.toLocaleString())
    this.resumeNoteTarget.classList.remove("hidden")
    this.flownOnTarget.value = this.resume.flight.flown_on
    this.flownOnTarget.disabled = true
    this.labelTarget.value = this.resume.flight.label || ""
    this.labelTarget.disabled = true
  }

  // The chip every upload in the app uses: blue with a spinner while
  // sending, gray with a document icon (and × to take it back) before.
  _chip(state, percent = null) {
    const file = this.file
    if (!file) return
    const sending = state === "uploading"
    const chip = document.createElement("span")
    chip.className = `inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded-lg text-[13px] ${sending ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-700"}`
    chip.innerHTML = sending
      ? '<svg class="animate-spin w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
      : '<svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'
    const label = document.createElement("span")
    label.className = "truncate"
    const name = `${file.name} · ${this._size(file.size)}`
    label.textContent = sending && percent != null ? `${this.textValue.uploading.replace("__PERCENT__", percent)} · ${name}` : name
    chip.append(label)
    if (state === "ready") {
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
      remove.setAttribute("aria-label", "Remove file")
      remove.textContent = "×"
      remove.addEventListener("click", () => this._clearFile())
      chip.append(remove)
    }
    this.fileChipsTarget.replaceChildren(chip)
    this.fileChipsTarget.hidden = false
  }

  async submitAdd() {
    if (this.photoSet || this.zipFile) return this._submitPhotos()
    const file = this.file
    if (!file) return this._addError(this.textValue.choose_file)
    if (!/\.(tiff?|jpe?g|png|webp)$/i.test(file.name)) return this._addError(this.textValue.file_type)
    const flownOn = (this.flownOnTarget.value || "").trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(flownOn)) return this._addError(this.textValue.date_needed)
    this._sending = true
    this.addButtonTarget.disabled = true
    this._addError(null)
    this._chip("uploading", 0)
    try {
      const created = await this._send("POST", this.urlValue, {
        label: this.labelTarget.value, flown_on: flownOn,
        filename: file.name, content_type: file.type || "application/octet-stream", size: file.size
      })
      await this._put(created.upload, file, (pct) => this._chip("uploading", pct))
      const done = await this._send("POST", `${this.urlValue}/${created.drone_flight.id}/complete`, {})
      if (this.loaded) {
        this.flights = [done.drone_flight, ...this.flights.filter((f) => f.id !== done.drone_flight.id)]
        this.flights.sort((a, b) => (b.flown_on > a.flown_on ? 1 : b.flown_on < a.flown_on ? -1 : b.id - a.id))
      } else {
        await this._load() // the first flight: the plan and the walks' routes come with it
      }
      if (!this.openValue) {
        this.openValue = true
        this._showInside()
        this._saveOpen()
      }
      this._sending = false
      this.closeAdd()
      showToast(this.textValue.added)
      this._paint()
    } catch (error) {
      this._addError(error.message || this.textValue.upload_failed)
      this._chip("ready")
      this.addButtonTarget.disabled = false
    } finally {
      this._sending = false
    }
  }

  // XMLHttpRequest, not fetch: only it reports upload progress.
  _put(upload, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(upload.method || "PUT", upload.url)
      Object.entries(upload.headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value))
      xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100)) }
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(this.textValue.upload_failed)))
      xhr.onerror = () => reject(new Error(this.textValue.upload_failed))
      xhr.send(file)
    })
  }

  _addError(message) {
    this.addErrorTarget.textContent = message || ""
    this.addErrorTarget.classList.toggle("hidden", !message)
  }

  // ── The drone's raw photos ──────────────────────────────────────────

  // The flight is made (or continued), its card says "Sending…", and the
  // photos go up a few at a time until every one is in the bucket; then
  // `complete` queues the stitch. A failed PUT waits and tries again for as
  // long as it takes (the note says so); Pause stops it where it is.
  async _submitPhotos() {
    const zip = this.zipFile
    const set = this.photoSet
    const flownOn = (this.flownOnTarget.value || "").trim()
    if (!this.resume && !/^\d{4}-\d{2}-\d{2}$/.test(flownOn)) return this._addError(this.textValue.date_needed)
    this._sending = true
    this.addButtonTarget.disabled = true
    this._addError(null)
    try {
      let flight
      let state
      if (this.resume) {
        flight = this.resume.flight
        state = this.resume.upload
        track("Drone Photos Upload Resumed", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, drone_flight_id: flight.id, kind: zip ? "zip" : "folder" })
      } else if (zip) {
        const created = await this._send("POST", this.urlValue, { kind: "zip", filename: zip.name, size: zip.size, flown_on: flownOn, label: this.labelTarget.value, email_when_done: this.emailWhenDoneTarget.checked })
        flight = created.drone_flight
        state = { ...created.upload, uploaded_parts: [] }
      } else {
        const created = await this._send("POST", this.urlValue, {
          kind: "photos", folder: set.folder, flown_on: flownOn, label: this.labelTarget.value, email_when_done: this.emailWhenDoneTarget.checked,
          files: set.files.map((f) => ({ name: f.name, size: f.size }))
        })
        flight = created.drone_flight
        state = { pending: set.files.map((f) => ({ name: f.name, size: f.size })) }
      }
      this._upsertFlight(flight)
      this.upload = { flight, kind: zip ? "zip" : "folder", total: zip ? zip.size : set.bytes, doneBytes: 0, inflight: new Map(),
                      done: 0, count: zip ? 0 : set.files.length, cancelled: false, xhrs: new Set(), startedAt: Date.now(), samples: [] }
      this.fileChipsTarget.hidden = true
      this.resumeNoteTarget.classList.add("hidden")
      this.progressTarget.classList.remove("hidden")
      this.pauseButtonTarget.textContent = this.textValue.pause
      this._note(null)
      this._paintProgress()
      window.addEventListener("beforeunload", this._unloadWarn)
      this._paint()

      if (zip) await this._uploadZip(zip, state)
      else await this._uploadPhotos(set, state)
      if (this.upload.cancelled) return this._pausedAt()

      const done = await this._completePhotos(zip, set)
      if (!done) return this._pausedAt()
      this._endUpload()
      this._upsertFlight(done.drone_flight)
      this._clearFile()
      this._sending = false // before closeAdd: it keeps the window open while photos are still going up
      this.closeAdd()
      showToast(this.textValue.photos_sent)
      this._paint()
    } catch (error) {
      if (this.upload?.cancelled) return this._pausedAt()
      this._endUpload()
      this._addError(error.message || this.textValue.upload_failed)
      this.progressTarget.classList.add("hidden")
      this.fileChipsTarget.hidden = false
      this.addButtonTarget.disabled = false
      this._paint()
    } finally {
      this._sending = false
    }
  }

  // Each photo: a signed link (asked for a batch at a time), one PUT with
  // retries, then its name goes in the next "received" batch. Only the
  // photos the server still waits for go up when continuing a flight.
  async _uploadPhotos(set, state) {
    const pending = new Set(state.pending.map((p) => p.name))
    const queue = set.files.filter((f) => pending.has(f.name))
    const upload = this.upload
    upload.count = set.files.length
    upload.done = set.files.length - queue.length
    upload.doneBytes = set.files.filter((f) => !pending.has(f.name)).reduce((sum, f) => sum + f.size, 0)
    const urls = new Map()
    let sent = []
    const flush = async () => {
      const names = sent
      sent = []
      if (!names.length) return
      const data = await this._sendRetry("POST", `${this.urlValue}/${upload.flight.id}/photos_received`, { names })
      upload.flight.received_count = data.received_count
      this._paintChips()
      // The server didn't find one: back in the queue it goes.
      for (const name of data.missing || []) {
        const file = set.files.find((f) => f.name === name)
        if (!file) continue
        urls.delete(name)
        queue.push(file)
        upload.done -= 1
        upload.doneBytes -= file.size
      }
    }
    const ensureUrl = async (name) => {
      if (urls.has(name)) return urls.get(name)
      const names = [ name, ...queue.filter((f) => !urls.has(f.name)).slice(0, URL_BATCH - 1).map((f) => f.name) ]
      const data = await this._sendRetry("POST", `${this.urlValue}/${upload.flight.id}/photo_urls`, { names })
      for (const u of data.urls || []) urls.set(u.name, u)
      return urls.get(name)
    }
    const worker = async () => {
      while (queue.length && !upload.cancelled) {
        const file = queue.shift()
        const link = await ensureUrl(file.name)
        if (!link) continue // the server already has it
        const ok = await this._putWithRetry(link, file, () => urls.delete(file.name) && ensureUrl(file.name))
        if (!ok) return
        upload.done += 1
        upload.doneBytes += file.size
        sent.push(file.name)
        this._paintProgress()
        if (sent.length >= RECEIVED_BATCH) await flush()
      }
    }
    await Promise.all(Array.from({ length: PHOTO_PARALLEL }, worker))
    if (!upload.cancelled) await flush()
  }

  // The zip in S3 parts: links for a batch of parts, each part one PUT with
  // retries; parts already in the bucket (continuing) are skipped.
  async _uploadZip(zip, state) {
    const upload = this.upload
    const have = new Set(state.uploaded_parts || [])
    const partSize = state.part_size
    const queue = Array.from({ length: state.part_count }, (_, i) => i + 1).filter((n) => !have.has(n))
    upload.doneBytes = Array.from(have).reduce((sum, n) => sum + Math.min(partSize, zip.size - (n - 1) * partSize), 0)
    const urls = new Map()
    const ensureUrl = async (n) => {
      if (urls.has(n)) return urls.get(n)
      const parts = [ n, ...queue.filter((m) => !urls.has(m)).slice(0, PART_BATCH - 1) ]
      const data = await this._sendRetry("POST", `${this.urlValue}/${upload.flight.id}/part_urls`, { parts })
      for (const u of data.urls || []) urls.set(u.part_number, { url: u.url, headers: {} })
      return urls.get(n)
    }
    const worker = async () => {
      while (queue.length && !upload.cancelled) {
        const n = queue.shift()
        const link = await ensureUrl(n)
        const blob = zip.slice((n - 1) * partSize, Math.min(n * partSize, zip.size))
        const ok = await this._putWithRetry(link, blob, () => urls.delete(n) && ensureUrl(n))
        if (!ok) return
        upload.doneBytes += blob.size
        this._paintProgress()
      }
    }
    await Promise.all(Array.from({ length: PART_PARALLEL }, worker))
  }

  // Everything went up: the server checks and queues the stitch. When it
  // finds photos (or parts) missing, those go up again, a few rounds at most.
  async _completePhotos(zip, set) {
    for (let round = 0; round < 4; round++) {
      try {
        return await this._send("POST", `${this.urlValue}/${this.upload.flight.id}/complete`, {})
      } catch (error) {
        if (!error.data?.missing) throw error
        const state = (await this._sendRetry("GET", `${this.urlValue}/${this.upload.flight.id}?upload=1`)).upload
        if (!state) throw error
        if (zip) await this._uploadZip(zip, state)
        else await this._uploadPhotos(set, state)
        if (this.upload.cancelled) return null
      }
    }
    throw new Error(this.textValue.upload_failed)
  }

  // One PUT, tried again after every failure (2 s, 4 s, ... up to a minute
  // apart) until it goes through or the upload is paused. A link that
  // expired (403) is asked for afresh. Resolves false when paused.
  async _putWithRetry(link, body, refresh) {
    let wait = 2000
    for (;;) {
      if (this.upload.cancelled) return false
      try {
        await this._putPart(link, body)
        this._note(null)
        return true
      } catch (error) {
        if (this.upload.cancelled) return false
        if (error.status === 403) link = (await refresh()) || link
        this._note(this.textValue.retrying)
        await new Promise((resolve) => setTimeout(resolve, wait))
        wait = Math.min(wait * 2, RETRY_MAX_MS)
      }
    }
  }

  _putPart(link, body) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const upload = this.upload
      xhr.open("PUT", link.url)
      Object.entries(link.headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value))
      xhr.upload.onprogress = (event) => {
        upload.inflight.set(xhr, event.loaded)
        this._paintProgress()
      }
      const done = (ok) => {
        upload.inflight.delete(xhr)
        upload.xhrs.delete(xhr)
        if (ok) resolve()
        else reject(Object.assign(new Error(this.textValue.upload_failed), { status: xhr.status }))
      }
      xhr.onload = () => done(xhr.status >= 200 && xhr.status < 300)
      xhr.onerror = () => done(false)
      xhr.onabort = () => done(false)
      upload.xhrs.add(xhr)
      xhr.send(body)
    })
  }

  // A small request tried again until it goes through (same waits as a PUT).
  async _sendRetry(method, url, body) {
    let wait = 2000
    for (;;) {
      if (this.upload?.cancelled) throw new Error("paused")
      try {
        const data = await this._send(method, url, body)
        this._note(null)
        return data
      } catch (error) {
        if (error.data?.error && error.status && error.status < 500 && error.status !== 403 && error.status !== 408 && error.status !== 429) throw error
        this._note(this.textValue.retrying)
        await new Promise((resolve) => setTimeout(resolve, wait))
        wait = Math.min(wait * 2, RETRY_MAX_MS)
      }
    }
  }

  // Pause stops the PUTs where they are; Continue sends what's left (the
  // same photos are still in hand, so no second drop is needed).
  pauseUpload() {
    const upload = this.upload
    if (!upload) return
    if (upload.cancelled) {
      // Continue: find out from the server what it still needs, then go on.
      this.upload = null
      this._findResume().then(() => this._submitPhotos())
      return
    }
    upload.cancelled = true
    upload.xhrs.forEach((xhr) => xhr.abort())
    track("Drone Photos Upload Paused", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, drone_flight_id: upload.flight.id, kind: upload.kind })
  }

  _pausedAt() {
    this._sending = false
    window.removeEventListener("beforeunload", this._unloadWarn)
    this.pauseButtonTarget.textContent = this.textValue.resume
    this._note(this.textValue.paused)
    this._paintChips()
  }

  // Remove the flight whose photos are going up (or are paused).
  async removeUploading() {
    const flight = this.upload?.flight
    if (!flight) return
    this.upload.cancelled = true
    this.upload.xhrs.forEach((xhr) => xhr.abort())
    try {
      await this._send("DELETE", `${this.urlValue}/${flight.id}`)
      this._endUpload()
      this.flights = this.flights.filter((f) => f.id !== flight.id)
      this._clearFile()
      this.closeAdd()
      showToast(this.textValue.removed)
      this._paint()
    } catch (error) {
      this._addError(error.message || this.textValue.remove_failed)
    }
  }

  _endUpload() {
    window.removeEventListener("beforeunload", this._unloadWarn)
    this.upload = null
    this.resume = null
    this.progressTarget.classList.add("hidden")
  }

  _unloadWarn = (event) => {
    if (!this.upload || this.upload.cancelled) return
    event.preventDefault()
    event.returnValue = this.textValue.leave_warning
    return this.textValue.leave_warning
  }

  _note(text) {
    this.progressNoteTarget.textContent = text || ""
    this.progressNoteTarget.classList.toggle("hidden", !text)
  }

  // "Sending 312 of 1,102 photos · 2.6 GB of 9.4 GB · about 40 min left".
  _paintProgress() {
    const upload = this.upload
    if (!upload) return
    const inflight = Array.from(upload.inflight.values()).reduce((sum, n) => sum + n, 0)
    const bytes = Math.min(upload.total, upload.doneBytes + inflight)
    const pct = upload.total ? Math.round((bytes / upload.total) * 100) : 0
    this.progressBarTarget.style.width = `${pct}%`
    const now = Date.now()
    upload.samples.push([ now, bytes ])
    while (upload.samples.length > 2 && now - upload.samples[0][0] > 60000) upload.samples.shift()
    const [ t0, b0 ] = upload.samples[0]
    const rate = now > t0 + 3000 ? (bytes - b0) / ((now - t0) / 1000) : 0
    const leftSeconds = rate > 0 ? (upload.total - bytes) / rate : null
    const left = leftSeconds !== null ? Math.ceil(leftSeconds / 60) : null
    const parts = [
      upload.kind === "zip"
        ? this.textValue.sending_zip.replace("__SENT__", this._size(bytes)).replace("__SIZE__", this._size(upload.total))
        : this.textValue.sending_photos.replace("__DONE__", upload.done.toLocaleString()).replace("__COUNT__", upload.count.toLocaleString())
                                       .replace("__SENT__", this._size(bytes)).replace("__SIZE__", this._size(upload.total))
    ]
    if (left !== null && !upload.cancelled) parts.push(this.textValue.time_left.replace("__MINUTES__", Math.max(1, left).toLocaleString()))
    this.progressTextTarget.textContent = parts.join(" · ")
    // The clock time the map should be ready (Phelipe 2026-10-28): the upload's rest, then the stitch.
    const photos = upload.kind === "zip" ? this._photosInZip(upload.total) : upload.count
    const ready = leftSeconds !== null && !upload.cancelled ? this._readyAround(leftSeconds + this._stitchSeconds(photos || 1), this.textValue.map_ready_around) : null
    this.readyAtTextTarget.textContent = ready || ""
    this.readyAtTarget.classList.toggle("hidden", !ready)
    this.readyAtTarget.classList.toggle("inline-flex", !!ready)
  }

  _upsertFlight(flight) {
    if (!this.loaded) this.flights = this.flights || []
    this.flights = [ flight, ...this.flights.filter((f) => f.id !== flight.id) ]
    this.flights.sort((a, b) => (b.flown_on > a.flown_on ? 1 : b.flown_on < a.flown_on ? -1 : b.id - a.id))
    if (this.upload?.flight?.id === flight.id) this.upload.flight = flight
  }

  // While the server gets a map ready, ask every few seconds.
  _startPolling() {
    if (this._polling) return
    this._polls = 0
    this._polling = setInterval(async () => {
      // A photo set this tab is sending is ours to update; a stitch is slow, so it's asked about less often and for as long as it runs.
      const stitching = this.flights.some((f) => f.status === "stitching")
      const tick = this._polls++
      const waiting = this.flights.filter((f) => (f.status === "processing" || f.status === "uploading" || f.status === "stitching") && f.id !== this.upload?.flight?.id)
                                  .filter((f) => f.status !== "stitching" || tick % STITCH_POLL_EVERY === 0)
      if ((!waiting.length && !stitching) || (!stitching && this._polls > POLL_MAX)) return this._stopPolling()
      for (const flight of waiting) {
        try {
          const data = await this._send("GET", `${this.urlValue}/${flight.id}`)
          const fresh = data.drone_flight
          if (!fresh) continue
          // A stitch moves through its stages with the status unchanged: the card's line follows the stage.
          const stageOnly = fresh.status === flight.status
          if (stageOnly && (fresh.stitch_stage || null) === (flight.stitch_stage || null)) continue
          this.flights = this.flights.map((f) => (f.id === fresh.id ? fresh : f))
          if (stageOnly) { this._paintChips(); continue }
          const base = this._base()
          if (fresh.status === "ready" && (!base || !base.corners)) this.picks = [fresh.id]
          this._paintChips()
          this._draw(this._base()?.id === fresh.id)
          this._paintAi()
        } catch (_) { /* ask again next time */ }
      }
    }, POLL_MS)
  }

  _stopPolling() {
    if (this._polling) clearInterval(this._polling)
    this._polling = null
  }

  // ── Remove ──────────────────────────────────────────────────────────

  askRemove() {
    if (!this._base()) return
    this.removeModalTarget.classList.remove("hidden")
  }

  cancelRemove() {
    this.removeModalTarget.classList.add("hidden")
  }

  // A failed flight made again from what was sent (Phelipe 2026-10-28): the photos go back to
  // the stitcher (at the lighter settings after a run out of memory), a sent map to the preparer.
  async retry() {
    const flight = this._base()
    if (!flight || flight.status !== "failed") return
    this.retryLinkTarget.disabled = true
    try {
      const data = await this._send("POST", `${this.urlValue}/${flight.id}/retry`)
      if (data.drone_flight) this.flights = this.flights.map((f) => (f.id === data.drone_flight.id ? data.drone_flight : f))
      this._paintChips()
      this._draw()
      this._startPolling()
      showToast(this.textValue.retry_started)
    } catch (error) {
      showToast(error.message || this.textValue.retry_failed, "error")
    } finally {
      this.retryLinkTarget.disabled = false
    }
  }

  // "Tell us more" under a failed flight: the Feedback panel opens as a bug about this flight, the words
  // ready to add to, and the note is tied to the item ConsLog opened itself (related_request_id).
  tellMore() {
    const flight = this._base()
    if (!flight || flight.status !== "failed") return
    const date = this._date(flight)?.toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric", year: "numeric" }) || ""
    window.dispatchEvent(new CustomEvent("instant-feedback:open", { detail: {
      mode: "text", prefill_kind: "bug",
      prefill_title: this.textValue.issue_title.replace("__DATE__", date),
      prefill: this.textValue.issue_prefill.replace("__DATE__", date).replace("__REF__", flight.issue_ref ? ` (${flight.issue_ref})` : ""),
      context: { drone_flight_id: flight.id, related_request_id: flight.issue_id || "" }
    } }))
    track("Drone Flight Issue Note Started", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, drone_flight_id: flight.id, has_issue: !!flight.issue_id })
  }

  async confirmRemove() {
    const id = this._base()?.id
    if (!id) return
    this.removeButtonTarget.disabled = true
    try {
      await this._send("DELETE", `${this.urlValue}/${id}`)
      this.flights = this.flights.filter((f) => f.id !== id)
      this.picks = this.picks.filter((p) => p !== id)
      if (!this.picks.length) {
        const first = this._shown()[0] || this.flights[0]
        if (first) this.picks = [first.id]
      }
      this.cancelRemove()
      showToast(this.textValue.removed)
      this._paint()
    } catch (error) {
      showToast(error.message || this.textValue.remove_failed, "error")
    } finally {
      this.removeButtonTarget.disabled = false
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _date(flight) {
    const [y, m, d] = String(flight.flown_on || "").split("-").map(Number)
    return y && m && d ? new Date(y, m - 1, d) : null
  }

  _day(flight) {
    const date = this._date(flight)
    if (!date) return ""
    const thisYear = date.getFullYear() === new Date().getFullYear()
    return date.toLocaleDateString(document.documentElement.lang || undefined, thisYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" })
  }

  _weekday(flight) {
    const date = this._date(flight)
    return date ? date.toLocaleDateString(document.documentElement.lang || undefined, { weekday: "short" }) : ""
  }

  // "Oct 16, 2026 · 7:48 AM · Foundations poured · DroneDeploy · 2.1 cm/px"
  _stamp(flight) {
    const lang = document.documentElement.lang || undefined
    const parts = []
    const date = this._date(flight)
    if (date) parts.push(date.toLocaleDateString(lang, { month: "short", day: "numeric", year: "numeric" }))
    if (flight.flown_time) parts.push(flight.flown_time)
    if (flight.label) parts.push(flight.label)
    if (flight.provider_label) parts.push(flight.provider_label)
    if (flight.cm_per_px) parts.push(this.textValue.cm_per_px.replace("__N__", flight.cm_per_px.toFixed(1)))
    if (flight.align_status === "aligned") {
      const other = this.flights.find((f) => f.id === flight.aligned_to_id)
      parts.push(this.textValue.aligned_with.replace("__DATE__", other ? this._day(other) : ""))
    } else if (flight.align_status === "nudged") parts.push(this.textValue.nudged)
    else if (flight.align_status === "weak") parts.push(this.textValue.align_weak)
    else if (flight.align_status === "failed") parts.push(this.textValue.align_failed)
    return parts.join(" · ")
  }

  _size(bytes) {
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`
    if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`
    return `${Math.max(1, Math.round(bytes / 1e3))} KB`
  }

  _teardown() {
    if (this.nudge) this._endNudge()
    clearTimeout(this.heightsWatch?.timer)
    this.heightsWatch = null
    this.reliefLayer = this.contoursLayer = this.waterLayer = this.targetLayer = null
    this.measuring = null
    this.measures = null
    this.photoLayer = null
    this.scrub = null
    this.scrubLayer = null
    this.baseLayer = null
    this.compareLayer = null
    this.planLayer = null
    this.routeLayer = null
    this._framed = false
    this.map?.remove()
    this.map = null
  }

  async _send(method, url, body) {
    const response = await fetch(url, {
      method, credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: body ? JSON.stringify(body) : undefined
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw Object.assign(new Error(data.error || this.textValue.upload_failed), { status: response.status, data })
    return data
  }
}
